// ============================================================================
// ui/product-step.js — Step 4: Request Quote (rebuilt 2026-08-05)
// ============================================================================
// Ryan's audit brief: *"a HUGE portion of what's included on there is redundant, not
// necessary, has poor UX, or sub-optimal UI. It needs to be brought to the same level as
// the other tabs, capture the relevant information that we need to capture from folks, and
// then be able to include that information on an email form to the rep assigned the lead
// and also in the DD itself. It'll end up operating very similar to the RFQ form, and
// should run on those same rails somehow… but tagged as a weight lead."*
//
// ── WHAT THIS TAB IS NOW ────────────────────────────────────────────────────────────────
// A quote form that asks ONLY what the design cannot already answer. Everything the buyer
// settled on an earlier tab is READ BACK, not re-collected (see ui/quote-summary.js):
//
//   the SET      from the Pick Weights tab  → shown, with a quantity per denomination
//   the ARTWORK  from Add Logo              → SENT, never shown and never re-asked
//   the COLOURS  from Customize Design      → SENT, never shown and never re-asked
//
// ⛔ SENT IS NOT SHOWN. The artwork and colours reach the REP in the lead; the buyer is not shown
// their own choices back (Ryan 2026-08-05: *"It's understood that this stuff will be included,
// redundant from the customers perspective. Less is more for them"*). Deleting a recap is not the
// same change as deleting a capture — see `captureDesign`.
//
// ⛔ THE PRODUCT PICKER IS GONE, and its removal is the biggest single fix. It re-asked
// category + kind that the first tab had already captured; picking a kind here called
// `switchProduct`, so the buyer could silently change the product they had just designed;
// and it carried ONE product while the first tab lets them choose any combination of four.
// A buyer who designed a three-product set could only ever quote one of them.
//
// ── THE RAILS ───────────────────────────────────────────────────────────────────────────
// Submit POSTs `/wd-quote`, which stores the design and calls `handleLeadIntake` in-process
// — the same pipeline every storefront RFQ uses, so the lead gets capacity-aware rep
// assignment, dedupe, the audit trail and the rep notification email. `source:
// 'weight-designer'` already resolves to the WEIGHTS group in the dashboard and is already
// inside the marketing view's online sources. The contact fields therefore match the
// storefront RFQ form's (`sections/custom-quote-form.liquid`) field for field — same names,
// same requirements, same timeline vocabulary — because two vocabularies in one dashboard is
// how a report starts lying.
// ============================================================================

import { h, replaceChildren } from './dom.js';
import * as artworkHold from './artwork-hold.js';
import { createQuoteAttach } from './quote-attach.js';
import { serializeProfile, displayWeightText, DEFAULT_BODY_COLOR, colorWord } from '../design-profile.js';
import { renderDesignToSVGString } from '../render-engine.js';
import { modelFor, geomOptsFor, defaultWeightFor, productSupportsBodyColor, baseProductKey } from '../products.js';
import { isPlaceholderText } from '../layouts.js';
import { openModal } from './modal.js';
import { inkContextKey, restoreInk, recascadeColorDefaults } from './state.js';
import { wdTrack, WD_EVENTS } from '../analytics.js';
import { isRepMode } from '../rep-mode.js';
import { canCreateQuote, createQuoteChooser } from './create-quote.js';
import { shareExistingLink, designSet } from './share.js';
import { quotedSet, artworkOnFile, designColors, describeQuantities, totalPieces, totalWeightLb, minimumFor, moqShortfall, setsFor, setRungs } from './quote-summary.js';
import { capturedUtm } from '../analytics.js';

// Local capture of the submitted design — the offline record, kept because it survives a
// failed POST and because `wd:quote-request` is a public hook an embedding page can listen to.
const QUOTE_KEY = 'wd:v2:quote-capture';
/* ⛔ localStorage, NOT sessionStorage, and that is the whole point (Ryan 2026-08-24). The capture
   above is per-tab scratch; THIS is the record that a request was sent, and it has to outlive the
   tab. A buyer who closes the designer, reopens it an hour later and finds their form armed again
   is the buyer who sends a second lead. `wd:v2:quote-sent` holds {ref, at, shareUrl, email}. */
const SENT_KEY = 'wd:v2:quote-sent';
const QUOTE_ENDPOINT = 'https://rfq-worker.ryan-29b.workers.dev/wd-quote';
/* Best-effort, fired just before the quote POST — see `uploadArtwork`. */
const ARTWORK_ENDPOINT = 'https://rfq-worker.ryan-29b.workers.dev/wd-artwork';

/* ⚠ THE STOREFRONT RFQ'S OWN VOCABULARY, verbatim (`sections/custom-quote-form.liquid`).
   The tab used to offer "Soon / This quarter / Exploring" — a third vocabulary for a field the
   dashboard groups and reports on, which makes a weight lead's timeline incomparable with every
   other lead's. If these ever need to change they change in both places, together. */
/** The tap affordance on a quantity thumbnail (phone) — a bare `+`, no lens and no handle (Ryan
 *  2026-08-09: *"the full magnifying glass look might be too much also"*). At 17px the glass ring
 *  and its handle were three strokes inside a circle that is itself a stroke, which reads as a smudge
 *  rather than an icon; one cross says "bigger" with a quarter of the ink.
 *  `stroke="currentColor"` so the badge's own `color` decides the contrast in one place. */
const ZOOM_ICON = '<svg viewBox="0 0 12 12" width="10" height="10" fill="none" aria-hidden="true">'
  + '<path d="M2.4 6 H9.6 M6 2.4 V9.6" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>'
  + '</svg>';

const TIMELINES = ['Immediately', '1-3 Months', '3-6 Months', '6+ Months', 'Just Exploring'];
const CUSTOMER_TYPES = ['Commercial', 'Residential'];

/* What a RESIDENTIAL buyer's Organization reads, verbatim from the storefront RFQ
   (`assets/custom-quote.js` → `initCustomerType`). A home-gym buyer has no organization, so the
   field is locked and stamped rather than left blank — the rep and the dashboard then see an
   explicit answer instead of an empty column they have to interpret as "didn't say" or "residence".
   ⚠ MUST MATCH THE STOREFRONT STRING. Both feed `handleLeadIntake` and land in the same DD field;
   two spellings of the same fact is how a report starts lying (the same argument the timeline
   vocabulary is held to). */
const RESIDENTIAL_ORG = 'n/a - residence';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/* ── PHONE MASK (Ryan 2026-08-07: *"only allow numbers in the phone number field, and as you type
   them, have it change 1234567890 to (123) 456-7890"*) ─────────────────────────────────────────
   Formats PARTIALS too, not just a finished number — the point is that an incomplete phone LOOKS
   incomplete. The submit gate needs 10 digits, and an unmasked field gave no clue whether you had
   given it 9 or 10, which is a required field failing silently. */
function formatPhone(digits) {
  const d = digits.slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function createProductStep(ctx) {
  // ── the form fields (storefront-RFQ parity) ────────────────────────────────────────────
  /* `ph || undefined` so an empty placeholder omits the ATTRIBUTE rather than setting it to ""
     (`h` skips null/undefined props). Ryan 2026-08-07 cleared the placeholders off every contact
     field — every one of them sits under its own label, so the ghost text was restating the label
     in six places. */
  const qinput = (id, ph, type = 'text', extra = {}) => h('input', { type, id, class: 'wd-qinput', placeholder: ph || undefined, ...extra });
  const nameInput = qinput('wd-q-name', '', 'text', { autocomplete: 'name' });
  const emailInput = qinput('wd-q-email', '', 'email', { autocomplete: 'email' });
  const phoneInput = qinput('wd-q-phone', '', 'tel',
    { autocomplete: 'tel', inputmode: 'numeric', maxlength: '14' });
  /* Non-digits are stripped on the way in, so letters simply never appear — `type=tel` allows them
     and `type=number` would have been worse (spinners, and it rejects the mask's own punctuation).
       The caret is repositioned by DIGIT COUNT rather than character offset: reformatting shifts
     every character right of an edit, so restoring the raw offset would drop the caret in the wrong
     place on any edit that isn't at the end. */
  let prevPhoneDigits = '';
  phoneInput.addEventListener('input', (e) => {
    const caret = phoneInput.selectionStart ?? phoneInput.value.length;
    let before = phoneInput.value.slice(0, caret).replace(/\D/g, '').length;
    let digits = phoneInput.value.replace(/\D/g, '').slice(0, 10);
    /* ⚠ BACKSPACE ONTO A SEPARATOR. Deleting the ")" in "(720) …" removes no digit, so the mask
       would rebuild it and the caret would land exactly where it started — the field reads as
       jammed. Same digit count after a backspace means a separator went, so take the digit before
       it instead, which is what the buyer was aiming at. */
    if (e.inputType === 'deleteContentBackward' && digits.length === prevPhoneDigits.length && before > 0) {
      digits = digits.slice(0, before - 1) + digits.slice(before);
      before -= 1;
    }
    prevPhoneDigits = digits;
    const masked = formatPhone(digits);
    phoneInput.value = masked;
    let pos = 0, seen = 0;
    while (pos < masked.length && seen < before) { if (/\d/.test(masked[pos])) seen++; pos++; }
    phoneInput.setSelectionRange(pos, pos);
  });
  const zipInput = qinput('wd-q-zip', '', 'text', { inputmode: 'numeric', maxlength: '5', autocomplete: 'postal-code' });
  /* ── ZIP → CITY, STATE (Ryan 2026-08-07: *"limit the zip to numbers only, and make sure it's
     hooked up like it is on other website forms"*) ────────────────────────────────────────────
     Mirrors the storefront RFQ's `initZipLookup` (`assets/custom-quote.js`): digits only, capped at
     5, a 500ms debounce, zippopotam.us, and the resolved place echoed back as "✓ Denver, CO" so the
     buyer can see we read their ZIP correctly. City/state also TRAVEL — see the payload.

     ⚠ ONE DELIBERATE DIVERGENCE FROM THE STOREFRONT. There, a ZIP that fails to resolve also blocks
     the submit (`zipValid` feeds `updateSubmitState`). Here it does not: that would put lead capture
     behind a third-party API's uptime, and a buyer who has designed a plate and filled the form is
     not someone to turn away because zippopotam.us is down or does not know a new ZIP. The gate
     stays "5 digits", and an unresolved ZIP still reaches the rep — flagged, not dropped. */
  const zipStatus = h('span', { class: 'wd-zip-status' });
  let zipCity = '';
  let zipState = '';
  {
    let timer = 0;
    /* Sequence guard: a slow lookup for an OLD ZIP must not land on a newer one. Type 80111, correct
       it to 80112, and without this the first response can arrive second and stamp the wrong city on
       the lead — a silent, plausible-looking wrong answer, which is the worst kind. */
    let seq = 0;
    zipInput.addEventListener('input', () => {
      const zip = zipInput.value.replace(/\D/g, '').slice(0, 5);
      if (zipInput.value !== zip) zipInput.value = zip;   // letters never survive a keystroke
      clearTimeout(timer);
      const mine = ++seq;
      zipCity = ''; zipState = '';
      if (zip.length < 5) { zipStatus.textContent = ''; zipStatus.className = 'wd-zip-status'; return; }
      zipStatus.textContent = 'Looking up…';
      zipStatus.className = 'wd-zip-status';
      timer = setTimeout(() => {
        fetch('https://api.zippopotam.us/us/' + zip)
          .then((r) => { if (!r.ok) throw new Error('not found'); return r.json(); })
          .then((d) => {
            if (mine !== seq) return;                      // a newer ZIP is already in flight
            const place = (d.places || [])[0] || {};
            zipCity = place['place name'] || '';
            zipState = place['state abbreviation'] || '';
            zipStatus.textContent = zipCity ? `✓ ${zipCity}, ${zipState}` : '';
            zipStatus.className = 'wd-zip-status is-success';
          })
          .catch(() => {
            if (mine !== seq) return;
            zipCity = ''; zipState = '';
            zipStatus.textContent = 'ZIP not found — we will confirm it with you.';
            zipStatus.className = 'wd-zip-status is-error';
          });
      }, 500);
    });
  }
  const orgInput = qinput('wd-q-org', '', 'text', { autocomplete: 'organization' });
  const streetInput = qinput('wd-q-street', '', 'text', { autocomplete: 'street-address' });
  const typeSelect = h('select', { id: 'wd-q-type', class: 'wd-qinput' },
    h('option', { value: '' }, 'Select one'),
    CUSTOMER_TYPES.map((t) => h('option', { value: t }, t)));
  /* "Select one" is a placeholder wearing an option's clothes, and a `<select>` has no
     `::placeholder` to hang that on — so it rendered in full text colour while every real
     placeholder on the form is muted (Ryan 2026-08-07). A class, not `:has(option[value='']:checked)`:
     the state is already known here and there is a `change` listener to hang it on, so nothing has to
     depend on selector support. */
  const syncTypePlaceholder = () => typeSelect.classList.toggle('is-placeholder', !typeSelect.value);
  typeSelect.addEventListener('change', syncTypePlaceholder);
  syncTypePlaceholder();

  /* ── ORGANIZATION IS A COMMERCIAL QUESTION (Ryan 2026-08-07) ────────────────────────────────
   * *"if someone picks 'Residence', there's going to be nothing for the Organization field. I think
   * we handled this already on the form at /pages/request-a-quote-form — might need to do a similar
   * thing here."*
   *
   * We did, and this is that rule copied deliberately rather than re-derived — `initCustomerType`
   * in the theme's `assets/custom-quote.js`. Both forms feed the same `handleLeadIntake` pipeline
   * and the same dashboard column, so they have to agree on what a residence's organization IS, not
   * merely on the fact that it has none.
   *
   *   Residential → locked, stamped `n/a - residence`, not required, any error cleared.
   *   Commercial  → unlocked, required, the sentinel wiped so nobody submits it as a company name.
   *   Unanswered  → locked, because the honest state of "which is it?" is that we do not know yet.
   *
   * ⚠ THE FIELD IS DISABLED, NOT HIDDEN. Hiding it would move every field below it the moment the
   * dropdown changes, on a form the buyer is halfway down. Locked-and-answered also SHOWS them what
   * we recorded, which a hidden field cannot.
   *
   * ⚠ AND `requiredFields` HAS TO ASK. Blanking the field is only half of it: the gate would still
   * hold the submit button down over a field the buyer is not allowed to type in — the exact
   * dead-button-with-no-explanation trap the blur validation was just added to close. See its entry
   * in that table. */
  /** The `*` beside the Organization label, handed back by `field()` so it can be hidden when the
   *  question does not apply. Null until the form body is assembled — `syncOrgField` guards it. */
  let orgLabelReq = null;
  const syncOrgField = ({ focus = false } = {}) => {
    const type = typeSelect.value;
    if (type === 'Commercial') {
      orgInput.disabled = false;
      if (orgInput.value === RESIDENTIAL_ORG) orgInput.value = '';
      if (focus) orgInput.focus();
    } else {
      orgInput.disabled = true;
      orgInput.value = type === 'Residential' ? RESIDENTIAL_ORG : '';
      clearError(orgInput);
    }
    if (orgLabelReq) orgLabelReq.hidden = type !== 'Commercial';
  };
  // `focus: true` only on a real change — a resume or the first paint must not steal the caret.
  typeSelect.addEventListener('change', () => { syncOrgField({ focus: true }); syncSubmitGate(); });
  const notesInput = h('textarea', { id: 'wd-q-notes', class: 'wd-qinput wd-qtextarea', rows: '3', placeholder: 'Do you need installation? Pantone numbers? Any questions for us?' });

  /* ⚠ NOT THE LOGO UPLOAD THAT WAS DELETED HERE ON 2026-08-05. That one asked the
     buyer to re-supply artwork the tool already holds; this is spare capacity for
     everything else (a vector version, a photo of plates to match, a PO) and its
     copy leads with "nothing is needed here". The distinction is the whole design
     — see the header of ui/quote-attach.js before changing a word of it. */
  const attach = createQuoteAttach();

  /* ── TIMELINE IS A DROP-DOWN, NOT A PILL ROW (Ryan 2026-08-09) ──────────────────────────────
   * *"On the mobile request quote form, make the TIMELINE portion a drop-down instead of pills.
   * Include the placeholder text of 'Select one' just like the Residential or Commercial drop-down
   * has."*
   *
   * 🔑 IT WAS THE ONE FIELD ON THIS FORM ANSWERING A CLOSED QUESTION IN A DIFFERENT CONTROL. Customer
   * type — same shape, one choice from a short fixed list — has been a `<select>` since the form was
   * built; timeline had five pills wrapping across two or three lines on a phone, which is both more
   * vertical space and a second interaction model for an identical question. Making them one control
   * is the whole ask.
   * ⚠ NOT PHONE-SCOPED, deliberately. Two different controls for one field across breakpoints would
   * mean two states to keep in step and two things to test; and a `<select>` is no worse on desktop,
   * where its twin already sits four fields above it.
   * ⚠ `timeline` STAYS A STRING OR `null`, which is what the payload and the reset both already
   * expect (`timeline` at submit, `syncTimeline` on refresh). An empty `<select>` reads as `''`, so
   * the `|| null` is what keeps "unanswered" spelled ONE way all the way to the worker. */
  let timeline = null;
  const timelineSelect = h('select', { id: 'wd-q-timeline', class: 'wd-qinput' },
    h('option', { value: '' }, 'Select one'),
    TIMELINES.map((t) => h('option', { value: t }, t)));

  /** `${productKey}|${weight}` -> count. The only genuinely NEW capture on this tab. */
  const quantities = Object.create(null);
  /** productKey -> true when the buyer pressed "Not sure yet". A THIRD state, distinct from an
   *  empty grid: it is an answer ("price it, we'll settle the count later"), not a silence. */
  const tbd = Object.create(null);
  let set = [];

  /* ── ONE DECISION BEFORE THIRTY BOXES (Ryan 2026-08-05, "the million dollar question") ───────
   * *"Do we simply collapse/hide ALL quantity stuff, and have 2 big noticeable buttons at the
   * beginning… if they click the first one, it bypasses opening all the fields for quantities so
   * they can expeditiously get through the form… but if they know exactly what they want, they can
   * click the 2nd button, expand all the qty fields, enter them now."*
   *
   * `null` until they choose, which is the whole point: the heaviest thing on this form is a
   * 30-box dumbbell rack, and asking an early-stage buyer to face it is how a finished lead becomes
   * an abandoned one. Two audiences, one question, and neither path is a dead end.
   *
   * ⚠ THE ANCHORING IS DELIBERATE. "Add quantities" is the accent button and carries the benefit
   * ("fastest turnaround"); "talk to a rep first" is quiet glass. If the escape hatch is the
   * loudest thing on the section, most people take it and we lose counts we would otherwise have
   * had — the point is to stop OVERWHELM costing us a lead, not to stop people answering.
   *
   * ⚠ AND IT IS RECORDED, not inferred. `quantityMode` travels in the payload so a rep can tell
   * "wants to talk first" (a position on a long-lead project) from "left it blank" (probably
   * missed it). Those need different first phone calls. */

  /* ── ⛔ AND THE QUESTION IS OFF (Ryan 2026-08-30) ─────────────────────────────────────────────
   * *"I want to do away with giving people the choice of 'I know what I need' and 'I'm not sure
   * yet'… We might bring it back later, so don't delete the code, just get rid of the choice and
   * default people to the screen they'd see if they were to click on 'I know what I need'."*
   *
   * ONE FLAG, and every word above stays true the moment it goes back to `true`. `renderMode` still
   * builds the pair, `syncGate` still opens the form on an answer, the `'rep'` branch still clears
   * the grid and still travels in the payload — nothing was removed, so nothing has to be rebuilt.
   * Flipping this line IS the restore.
   *
   * ⚠ THE DEFAULT HAS TO MOVE WITH IT, and that is not tidiness. With the chooser hidden there is
   * no way left to answer the question, and `isComplete` requires an answer — so `null` would hold
   * the submit button greyed out forever with nothing on screen that could ever un-grey it. 'enter'
   * IS the answer now, and it is the one the two buttons were already anchored toward.
   *
   * ⚠ AND THE MOQ IS WHY THE ESCAPE HATCH CANNOT SIMPLY COME BACK ALONE. 'rep' clears every
   * quantity, so a set in that mode can never reach the minimum; the gate exempts it explicitly
   * (see `isComplete`). Re-enable this and that exemption is what keeps the hatch usable. */
  const MODE_CHOICE_ENABLED = false;
  let quantityMode = MODE_CHOICE_ENABLED ? null : 'enter'; // null | 'enter' | 'rep'

  const setRows = h('div', { class: 'wd-qty-set' });
  /* ── THE QUANTITY GRIDS SLIDE (Ryan 2026-08-07: *"when you click the 'I know what I need' button…
   * make whatever expands down below it smooth with a slick animation like so many other things in
   * the WD. Same if you then click I'm not sure yet, it should slide up"*) ──────────────────────
   *
   * `setRows.hidden` was `display: none` — one frame, on and off. The height cannot be animated
   * directly either: it is whatever three products' worth of number grids need, so there is no
   * start or end value to transition between.
   *   So the WRAPPER is a one-row grid animating `grid-template-rows` 0fr → 1fr. The track knows
   * the content's real height without anyone measuring it, and the same trick already drives the
   * Pick Weights accordion and the nav footer's collapsing button, so this is the house recipe
   * rather than a third way of doing it. */
  const setReveal = h('div', { class: 'wd-qty-reveal' }, h('div', { class: 'wd-qty-reveal__in' }, setRows));
  /** How long `.wd-qty-reveal` takes to open or close — must match the CSS transition. Declared
   *  beside the element rather than beside `refresh`, so `renderSet` can never read it in the TDZ. */
  const QTY_SLIDE_MS = 340;
  /* ── NOTHING BUT THE QUESTION UNTIL IT IS ANSWERED (Ryan 2026-08-07) ──────────────────────────
   * *"make it so the form can't even be seen until they select 'I know what I need' or 'I'm not sure
   * yet'… the question and the 2 pill buttons should be centered vertically on the page. then once
   * they click one, it should slide up to the top and the rest of the YOUR DETAILS should all
   * cleanly slide up."*
   *
   * ⚠ THIS WAS ARGUED AGAINST ONCE AND THE ANSWER CHANGED — record why, so it is not re-litigated.
   * When gating was proposed INSTEAD of validating, it was the wrong trade: it taxed every buyer to
   * catch the few who skip, and did not even close the hole ("I know what I need" over an empty grid
   * is still a blank lead). The submit gate landed first, so the question is now REQUIRED either way
   * — which means the click is no longer optional and hiding the form costs the buyer nothing. What
   * it buys is a first impression of ONE question instead of ten fields.
   *
   * Same `0fr → 1fr` grid track as `.wd-qty-reveal` above rather than a third mechanism — the track
   * knows the form's real height without anyone measuring it. */
  const formReveal = h('div', { class: 'wd-form-reveal' });
  /** Must match the `min-height` transition on `.wd-step--product.is-ungating` in the stylesheet. */
  const GATE_SLIDE_MS = 460;
  let gateTimer = 0;
  let clearRowsTimer = 0;
  let settleTimer = 0;
  const setEmpty = h('p', { class: 'wd-empty-hint' }, 'Pick your equipment on the Pick Weights tab and it will appear here.');
  const qtyTotal = h('p', { class: 'wd-qty-total' });
  /* ── THE TARGET, AND HOW FAR OFF IT IS (Ryan 2026-08-30) ────────────────────────────────────
   * *"They'll need to see the minimum amount written somewhere, and every time they update a
   * quantity it'll need to reduce the number of lbs needed to hit the minimum, and then also have a
   * count somewhere showing what they've added in lbs."*
   *
   * Its own line rather than more text on `qtyTotal`, because the two say different KINDS of thing:
   * that line reports what you have (pieces, pounds), this one reports what the order still needs.
   * Folding them together is how the target ends up buried in a sentence about piece counts.
   *
   * ⚠ IT SHOWS BEFORE THERE IS ANYTHING TO COUNT. `qtyTotal` hides at zero — a total of nothing is
   * not worth a line — but a minimum nobody has met yet is exactly when the number matters most.
   * A buyer should learn the target while filling the grid, never at the button. */
  const moqLine = h('p', { class: 'wd-qty-moq' });
  /* ONE LINE ON DESKTOP, TWO ON A PHONE (Ryan 2026-08-30: *"any way on desktop we can get all of
     this onto one clean on-brand line"*). A wrapper rather than one merged string, because the two
     halves answer different questions and hide independently — `qtyTotal` goes when there is
     nothing counted, `moqLine` stays because a target nobody has met yet is exactly when it matters.
     The flex row and the interpunct between them are the stylesheet's job; the separator is drawn by
     `.wd-qty-total:not([hidden]) + .wd-qty-moq`, so it cannot dangle in front of a hidden total.
     ⚠ Neither `<p>` may be given an explicit `display` — that would beat the `[hidden]` attribute
     they are toggled with, and the total would never actually hide. */
  const qtyMeta = h('div', { class: 'wd-qty-meta' }, qtyTotal, moqLine);
  /* `id` + `tabindex` exist for the validation path: `markError`/`clearError` resolve an error node
     by `${control.id}-error`, and `firstBad.focus()` needs something focusable to scroll to. -1 keeps
     it out of the tab order — it is a label for two real buttons, not a stop of its own. */
  const modeChooser = h('div', { class: 'wd-qmode', id: 'wd-q-mode', tabindex: '-1' });
  /* OUTSIDE `modeChooser`, deliberately: `renderMode` calls `replaceChildren` on it, which would
     destroy an error node parked inside on the next re-render. */
  const modeError = h('div', { class: 'wd-qerror', id: 'wd-q-mode-error', hidden: true });
  // "Submit quote request", not "Request my quote" (Ryan 2026-08-05) — the same correction as the
  // section heading: they submit a request, WE quote.
  /* ── IT WEARS THE NAV BUTTON'S OWN CLASSES (Ryan 2026-08-07: *"make the 'Submit quote request'
   * button have the same glass-like appearance as the back button next to it"*) ────────────────
   * `.wd-panenav__btn` + the flat-glass pair, not a copy of their declarations — so the fill, the
   * lit top edge, the bevel, the hover lift and the 0.42 disabled fade are literally the rules the
   * button beside it runs. `.wd-btn--primary` is dropped rather than kept alongside: it is defined
   * after `.wd-flat-glass` in the sheet, so its flat blue would simply win and the glass would
   * never show — the same trap the gate's own buttons document.
   *   `--flush` because this sits ON the pane, which is glass itself (the WD glass rule).
   */
  const quoteBtn = h('button', {
    type: 'button',
    class: 'wd-btn wd-panenav__btn wd-flat-glass wd-flat-glass--flush wd-quote-btn',
    onClick: requestQuote,
  }, 'Submit quote request');

  /* ── WHY THE BUTTON IS GREY (Ryan 2026-08-30) ───────────────────────────────────────────────
   * *"If they fill out all the mandatory (*) fields on the questionnaire but still haven't hit the
   * minimum weight amount yet, we need to make this super clear somehow."*
   *
   * The one failure this whole element exists to prevent: every star answered, nothing red on the
   * form, and a dead submit button with no stated reason. The blocker has to name itself where the
   * frustration is — the countdown up in the quantities section has usually been scrolled past by
   * the time someone reaches the button.
   *
   * ⚠ ITS POSITION IS THE LAST THING IN THE FORM, WHICH IS ADJACENT TO THE BUTTON ON BOTH LAYOUTS,
   * and that is the only place one node can be. On a phone the button is mounted right below this
   * (see the submit-row note); on desktop the button is MOVED into the pane's nav footer, directly
   * under the form — so "last line of the form" reads as "just above the button" either way. A
   * second copy beside the desktop button is not an option: `quoteBtn` is a move, not a copy, and
   * a node cannot be in two places. */
  const moqNotice = h('div', { class: 'wd-qerror wd-moq-notice', hidden: true });

  const field = (labelText, control, opts = {}) => {
    const err = h('div', { class: 'wd-qerror', id: control.id ? `${control.id}-error` : undefined, hidden: true });
    // `opts.reqMark` hands the caller the `*` element so it can hide it when the field stops being
    // required — Organization, which is a commercial-only question. A star over a locked field is
    // the form asking for something it will not let you give.
    const reqMark = opts.required ? h('span', { class: 'wd-req' }, '*') : null;
    if (reqMark && opts.onReqMark) opts.onReqMark(reqMark);
    const wrap = h('div', { class: 'wd-qfield' + (opts.half ? ' is-half' : '') },
      h('label', { class: 'wd-label', for: control.id || undefined },
        labelText, reqMark),
      control,
      opts.help ? h('div', { class: 'wd-qhelp' }, opts.help) : null,
      err);
    // Inline, and cleared as soon as the buyer starts fixing it. The old tab reported every
    // problem as a one-line status message at the other end of the pane, which on a long form
    // means being told something is wrong with no indication of where.
    control.addEventListener('input', () => clearError(control));
    control.addEventListener('change', () => clearError(control));
    return wrap;
  };
  const errorOf = (control) => (control.id ? document.getElementById(`${control.id}-error`) : null);
  function clearError(control) {
    const e = errorOf(control);
    if (e) { e.hidden = true; e.textContent = ''; }
    control.classList.remove('is-invalid');
  }
  function markError(control, message) {
    const e = errorOf(control);
    if (e) { e.textContent = message; e.hidden = false; }
    control.classList.add('is-invalid');
  }

  /* `title` is optional — a section whose body already opens with its own heading passes null rather
     than rendering an empty `<h3>` (which would still take its margin and leave a phantom gap). */
  const section = (title, sub, ...body) => h('div', { class: 'wd-qsection' },
    title ? h('h3', { class: 'wd-qsection-title' }, title) : null,
    sub ? h('p', { class: 'wd-qsection-sub' }, sub) : null,
    ...body);

  const setSection = section(null, null,
    setEmpty, modeChooser, modeError, setReveal, qtyMeta);

  /* ── THE REP'S HANDOFF ─────────────────────────────────────────────────────────────────────
     Null for a buyer, so the whole feature is one absent node off the portal rather than a hidden
     one — nothing to reveal by accident, and nothing in the tab order.

     ⛔ THE THREE OPTIONS ARE THE SECTION (Ryan 2026-09-01: *"Skip the create quote button. Just
     have the 3 options right there on the screen under the quantity selector."*). This was a
     titled card whose button opened a dialog containing them; the dialog is gone and the choices
     sit on the panel that had room for them all along.

     ⚠ `set` and `quantities` are passed as a THUNK, not by value. They are this closure's own and
     they MUTATE as the rep types — handing the chooser a snapshot at render time would send the
     Workstation whatever the grid held when step 4 first painted, which for the common case (open
     the tab, type the counts, commit) is nothing at all. */
  const repChooser = canCreateQuote()
    ? createQuoteChooser(ctx, () => ({ set, quantities }))
    : null;
  const repCreateSection = repChooser ? section(null, null, repChooser.el) : null;

  /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-08-24) — ⭐ THE SUBMITTED STATE.
   *
   * Allison Rivas sent the same design twice, 5m24s apart (RW-1339-A / RW-1340-A), having changed
   * nothing: the stored envelopes were byte-identical but for their capture timestamp. The
   * designer's answer to a successful submit was a modal, and behind the modal the form came back
   * fully armed with every field still filled. Nothing on screen said she had already sent it, and
   * no email had arrived to say so either. Pressing the button again is the correct reading of
   * that UI.
   *
   * ⛔ THE FORM IS REPLACED, NOT JUST DISABLED. A greyed-out submit button beneath a filled-in
   * form is ambiguous in exactly the wrong direction: it looks like the form is broken, which
   * invites a reload, and a reload used to bring the armed form straight back. The RFQ form has
   * always done it this way (`custom-quote.js` hides `.layout`, shows `.success`) and it is the
   * closest sibling this tab has.
   *
   * ⛔ AND IT MUST NOT BE A DEAD END. This is a DESIGNER, not a checkout — a buyer who just
   * requested plates may legitimately want to design a rack next, and the storefront form's
   * "you're done, go away" shape would strand them in the tool with no way back. "Start a new
   * request" is the unconditional exit; the state is a latch, never a wall. */
  const sentLinkRow = h('div', { class: 'wd-sent__links' });
  /* No `hidden` attribute: `.wd-sent` is `display:none` in the sheet and `.is-sent .wd-sent` turns
     it on, so one class on the step root owns which of the two states is mounted.
     ⛔ NO REFERENCE NUMBER (Ryan 2026-08-24: *"that's fine on the email, but overkill on the thank
     you for submitting page"*). It is in their inbox if they ever want it, and on screen it mainly
     invites a call about a ref no rep has opened yet.
     ⛔ AND NO PROOF PROMISE. It lived here for one round, carried over from the deleted modal; Ryan
     cut it. It still runs in the confirmation email as step 4 of What Happens Next, which is where
     the sequence explains itself. */
  const sentCard = h('div', { class: 'wd-qsection wd-sent' },
    h('div', { class: 'wd-sent__check', 'aria-hidden': 'true' }, '✓'),
    h('h3', { class: 'wd-sent__title' }, 'Thank you for your submission!'),
    h('p', { class: 'wd-sent__body' },
      'We will be reaching out to answer any questions and provide you with a quote.'),
    sentLinkRow,
  );

  const el = h(
    'div',
    { class: 'wd-step wd-step--product' },

    // ── 1. WHAT YOU'RE QUOTING — the set, with quantities ────────────────────────────────
    /* ⚠ NOT "What you're quoting" (Ryan 2026-08-05: *"the header 'What you're quoting' is
     * misleading -- blue sky is quoting, not the customer filling out the field"*). WE quote; the
     * buyer requests. The heading is in their voice, so it has to keep the roles straight -- which
     * is also what the button under it says: "Request my quote".
     *   And no sub-line: *"remove 'Your design carries across everything you pick.' -- that's
     * implied"*. They are looking at their own design on every product listed. */
    /* ⚠ NO SECTION HEADING HERE, deliberately (Ryan 2026-08-07: *"there are 2 headers here…
     * drop the top one, keep the bottom one"*). "Quantities you'd like quoted" and the mode
     * chooser's own "What quantities do you need?" were two headings stacked, saying the same
     * thing twice. The chooser's question IS this section's heading now, and it wears
     * `.wd-qsection-title`'s rules to prove it (see `.wd-qmode-q` in the stylesheet) — so
     * re-adding a title here would put the pair straight back. */
    /* ⭐ THE REP'S OWN EXIT, ABOVE THE CUSTOMER FORM (Ryan 2026-08-30) ──────────────────────────
     * *"if a rep is making a design in the portal and they click on the Create Quote tab… it will
     * automatically link the file to the chain."*
     *
     * ⛔ ADDED BESIDE THE FORM, NOT INSTEAD OF IT. This step collects a CUSTOMER's details and
     * files a lead; a rep starting a quote they will price themselves is a different act with a
     * different destination. But a rep ALSO takes requests over the phone, and that is this form.
     * Two audiences, two exits, one step — the same shape the mode chooser used to have.
     *
     * First, because a rep who came here to build a quote should not have to scroll past a
     * customer questionnaire to find the button that does it. Absent entirely for buyers:
     * `canCreateQuote()` is false off the portal, so this is one `null` in the tree. */
    /* CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-01) — ⛔ A REP DOES NOT WALK A CUSTOMER'S
       QUOTE REQUEST. This step used to render, for everyone, the buyer's quantity grid, the "Your
       details" questionnaire and a "Submit quote request" button, with the rep's card added ABOVE
       them. Ryan, on the live tool: *"it still brings up the box to type in all the quantities as
       if you were a customer inquiring for pricing... It also brings up the form questionnaire to
       enter all your information, and the 'Submit quote request' button at the bottom - also not
       necessary."*

       ⚠ THIS REVERSES A DELIBERATE 2026-08-24 DECISION, whose note read *"added beside the form,
       not instead of it... a rep ALSO takes requests over the phone, and that is this form."* The
       reasoning was sound and the outcome was not: the form is a lead-filing act with a different
       destination, and pricing it up in the Workstation is what a rep actually does. The phone case
       is not lost, it is just no longer this screen's job.

       ⚠ THE QUANTITY GRID STAYS, and that is the one thing NOT mirrored from the Gym Layout
       Designer. A floor plan already carries its items, so the GLD's rep has nothing to enter; a
       weight design carries denominations but no counts, and `quantities` is built in this closure
       and exists nowhere else. Drop it and `orderedForQuote` returns [] — every row would land
       unpriced and the rep would retype twelve denominations in the Workstation. Ryan chose the
       compact entry over that.

       ⚠ AND THE ORDER FLIPS. The card sat first so a rep would not scroll past a questionnaire to
       reach it; with the questionnaire gone the natural order is the honest one — set the
       quantities, then build the quote from them. */
    ...(isRepMode() ? [setSection, repCreateSection] : [repCreateSection, setSection]),

    /* ⛔ NO "YOUR DESIGN" SECTION, AND NO SECOND ARTWORK ASK (Ryan 2026-08-05: *"don't need to
     * include 'YOUR DESIGN' stuff on this page (colors and logo). It's understood that this stuff
     * will be included, redundant from the customers perspective. Less is more for them. And also
     * get rid of the logo upload. they would have uploaded their best logo (presumably)… Same with
     * pantone numbers. if they have that, they can drop it in the notes."*)
     *
     * ⚠ THE DATA STILL TRAVELS. `captureDesign` keeps sending `colors` and `artwork` — the REP
     * needs both, and the worker renders them into the lead. What went is the buyer being shown
     * their own choices back and asked to confirm them. Deleting the section is not the same
     * change as deleting the capture, and conflating the two is how a rep quietly stops receiving
     * a colour to match. */

    // ── 2. YOUR DETAILS — storefront-RFQ parity, behind the reveal ───────────────────────
    // ⛔ Absent for a rep, not hidden: one missing node rather than a concealed one, so there is
    //    nothing to reveal by accident and nothing in the tab order.
    isRepMode() ? null : formReveal,

    // ── 3. SENT — replaces both of the above once a request lands. Hidden until then. ────
    //    Unreachable in rep mode anyway (`markSubmitted` returns early), and absent for the same
    //    reason as the form.
    isRepMode() ? null : sentCard,
  );

  /* Mounted into the reveal rather than straight into `el`, so the whole form can slide as one
     block. `__in` carries the clip: a grid track is exactly content-height, and clipping at the
     TRACK would shear the fields' focus rings — the same reason `.wd-qty-reveal` has its own inner. */
  formReveal.append(h('div', { class: 'wd-form-reveal__in' },
    section('Your details', null,
      h('div', { class: 'wd-qgrid' },
        field('Name', nameInput, { half: true, required: true }),
        field('Email', emailInput, { half: true, required: true }),
        field('Phone', phoneInput, { half: true, required: true }),
        field('ZIP', zipInput, { half: true, required: true, help: zipStatus }),
      ),
      h('div', { class: 'wd-qgrid' },
        field('Residential or Commercial', typeSelect, { half: true, required: true }),
        field('Organization', orgInput, { half: true, required: true, onReqMark: (n) => { orgLabelReq = n; } }),
      ),
      /* No `help` on anything here (Ryan 2026-08-05: *"remove sub-text like this: 'Optional —
         helps us quote freight accurately.'"*). A label that needs a sentence under it is a label
         that should be rewritten; every field on this form is self-evident. */
      field('Street Address', streetInput),
      field('Timeline', timelineSelect),
      field('Anything else we should know?', notesInput),
      field('Attach files (optional)', attach.el)),
    moqNotice,
    /* ⛔ `quoteBtn` IS HERE FOR THE PHONE, AND ONLY THE PHONE (Ryan 2026-08-09: *"still not seeing
       the submit button… my expectation is to see it below the 'anything else we should know' input
       box — but there's nothing below it, can't even scroll much beyond it."*)

       🔑 IT HAD NO HOME ON MOBILE AT ALL, WHICH IS WHY NOTHING WAS "COVERING" IT. The 2026-08-07 note
       this replaces said the button is handed to the pane's nav footer — true, and the footer is
       built by `createPaneNav`, which main.js gates behind `!isPhone()`. On a phone that call never
       runs, so `forwardSlot` is never asked for the button and `footerAction` is handed to nobody.
       It was not hidden, clipped or disabled; it was never mounted. (The phone's forward control is
       the ‹ prev | next › pill row in the sheet header instead — and on the last step there is no
       "next", which is exactly the gap the desktop footer was filling.)

       So on a phone it goes where Ryan expects it: last thing in the form, under the notes box. It
       is still a MOVE and never a copy — desktop appends the same node into the footer, and a node
       cannot be in two places, so the two surfaces cannot both show one.
       ⚠ The gate is `isDesktopPane()`'s twin, matched here rather than imported, because this module
       has no phone helper of its own and one bare media query is cheaper than threading a flag
       through the constructor. It is read at BUILD time; the panel is rebuilt on a step change, and
       nobody resizes a phone into a desktop mid-quote. */
    (typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(max-width: 768px) and (pointer: coarse)').matches)
      ? h('div', { class: 'wd-qty-submit-row' }, quoteBtn)
      : null,
  ));

  timelineSelect.addEventListener('change', () => {
    timeline = timelineSelect.value || null;
    syncTimeline();
  });
  /** Push `timeline` back onto the control and mute the placeholder — the same two jobs the pill row
   *  did, and the same muting `syncTypePlaceholder` does for customer type (a `<select>` has no
   *  `::placeholder`, so the class is how "Select one" reads as a prompt rather than an answer). */
  function syncTimeline() {
    timelineSelect.value = timeline || '';
    timelineSelect.classList.toggle('is-placeholder', !timelineSelect.value);
  }

  // ── the quantity grid ──────────────────────────────────────────────────────────────────
  /**
   * One block per product in the set: its ladder, a count per rung, and a "fill the rack"
   * shortcut.
   *
   * ⚠ THE LADDERS ARE UNEVEN AND SOME ARE LONG — bumper plates carry five or six rungs, the
   * dumbbell rack runs 5–150 in fives with rubber-only half-steps (~35). A single flat grid of
   * every denomination would be a wall, so each product gets its own collapsible block and the
   * rungs wrap in columns.
   */
  /**
   * THE DESIGN, ON THAT PRODUCT (Ryan 2026-08-05: *"add an SVG to each item they request a quote
   * for on the form, so it's fresh in their minds what each quantity fields apply to which
   * design(s)"*).
   *
   * Rendered from the live profile through the same engine the stage uses, so it is the buyer's
   * actual artwork on that product's actual face — not a category icon. Cheap: an SVG string into
   * an `<img>`, no raster, no Three.js.
   *
   * ⚠ THE DENOMINATION MATTERS. The grip family is two different faces under one product (2.5 and
   * 5 lb are cutout-free), so a thumbnail drawn at the wrong weight shows hand holes a plate does
   * not have. Uses the buyer's current weight when this product offers it, and the product's own
   * default when it does not.
   * ⚠ AND A NON-DYEABLE PRODUCT RENDERS BLACK whatever the design is dyed — the thumbnail must not
   * promise a colour the product cannot take (the same call `makeTrayThumb` makes).
   */
  /** The denomination this product's thumbnail is drawn at — the buyer's current one where this
   *  product offers it, its own default where it does not. Named because the ZOOM has to say it:
   *  the plate carries a printed number, so a picture of it is a picture of one rung. */
  function thumbWeight(item) {
    /* ⭐ A CARD SHOWS A PLATE IT WAS DESIGNED ON, when it knows one (Ryan 2026-08-10, describing the
       fix he expected: *"one showing the yellow for the 45s and green for the 35s"*). Without this
       both cards print the LIVE denomination, so two sets differing only by rung showed the same
       number — the picture said nothing about which card it belonged to.
       ⚠ `designedRungs` is populated only where a product yields more than one card, so the ordinary
       single-design quote keeps the previous behaviour exactly: the buyer's current rung if this
       product offers it, else the product's own default. */
    const own = (item.designedRungs || []).find((w) => item.weights.includes(w));
    if (own != null) return own;
    return item.weights.includes(ctx.state.ui.weight) ? ctx.state.ui.weight : defaultWeightFor(item.key);
  }

  /**
   * The design AS THIS CARD WEARS IT — plate colour AND the ink that belongs to that plate.
   *
   * ⛔ THE PLATE COLOUR ALONE IS NOT THE CONFIGURATION (Ryan 2026-08-10, the Wallabies design: a
   * green plate with yellow lettering, and the inverse on the 45). Overriding only the body left the
   * 45's card wearing the GREEN configuration's ink on a yellow plate — so the yellow lettering sat
   * on yellow, the render engine's contrast rescue caught the TEXT and swapped it to black, and the
   * logo (which that rescue does not reach) simply disappeared into the background. One cause, both
   * symptoms: *"the SVG for 45 shows a yellow plate with black text and no logo."*
   *   Ink is remembered per PLATE COLOUR (`savedInk`, see the block in ui/state.js), so drawing a
   * configuration means asking for its bucket, not just repainting the body underneath it.
   *
   * ⛔ AND IT MUST NOT TOUCH THE LIVE DESIGN. `restoreInk` MUTATES the profile it is handed — it is
   * built for the seams that really are switching product — so this hands it a copy. A thumbnail
   * that silently rewrote the buyer's fills would be the worst bug in the tool and the hardest to
   * attribute, since nothing would look wrong until they went back to the plate they had left.
   * ⚠ THE COPY KEEPS THE LOGO BY REFERENCE. `e.logo.dataUrl` is a multi-MB string and this runs per
   * card per repaint; `restoreInk` writes `e.logoAddOutline` and the element's fill refs, never
   * anything inside `e.logo`, so sharing that object is safe and skipping it is the difference
   * between a cheap clone and copying the artwork four times a paint.
   * ⚠ IDENTICAL CONTEXT SHORT-CIRCUITS. A card whose plate matches the live one needs no copy at all,
   * which is every single-configuration quote — i.e. almost all of them.
   */
  function profileForCard(item) {
    const live = ctx.state.designProfile;
    const want = inkContextKey(ctx.state, { body: item.plateHex, product: item.key });
    if (want === inkContextKey(ctx.state)) return live;
    const els = live.elements || [];
    const held = els.map((e) => (e && e.logo ? e.logo : null));
    const bare = els.map((e, i) => (held[i] ? { ...e, logo: null } : e));
    let copy;
    try {
      copy = { ...live, elements: structuredClone(bare) };
    } catch {
      return live;   // a clone we cannot make is not worth a wrong thumbnail AND a corrupted design
    }
    /* ⛔ THE PEELED RASTER BELONGS TO THE PLATE IT WAS MADE FOR — the fifth bug of this exact shape
       (memory `wd-renderurl-transient-repeat-offender`). `drawLogo` draws
       `logo.renderUrl || logo.dataUrl`, and `renderUrl` is a TRANSIENT recolour that `syncLogoPeel`
       regenerates for whatever plate is LIVE. Sharing the logo object by reference — which is what
       keeps this clone cheap — shared that raster too, so the yellow card drew the GREEN
       configuration's logo: yellow ink on a yellow plate, i.e. nothing at all.
       ⭐ SO ASK FOR *THIS* PLATE'S. `ctx.logoRasterFor` returns the composite `syncLogoPeel` built
       when the buyer was last standing on this configuration — real work, already paid for, that
       used to be discarded on the way out. A miss falls back to the plain artwork, which is visible
       and honest even if it is not recoloured.
       ⚠ A SHALLOW COPY either way, so the multi-MB `dataUrl` is still shared and only the two
       transient fields move. `renderAspect` travels WITH `renderUrl` and never without it: an added
       ring pads the raster, and a padded aspect behind a fallback image stretches the mark. */
    const raster = ctx.logoRasterFor ? ctx.logoRasterFor(want) : null;
    copy.elements.forEach((e, i) => {
      if (!held[i]) return;
      e.logo = raster && raster.url
        ? { ...held[i], renderUrl: raster.url, renderAspect: raster.aspect }
        : { ...held[i], renderUrl: null, renderAspect: null };
    });
    /* ⛔ BODY FIRST, THEN INK — the derivation below reads the plate it is deriving against. */
    copy.bodyColor = item.plateHex;
    /* ⭐ REMEMBERED IF WE HAVE IT, DERIVED IF WE DO NOT (Ryan 2026-08-10: *"the yellow loaded with
       black text… despite the 3D model showing it all as green on the yellow plate"*).
         `savedInk` is written at a SEAM — when the buyer leaves a plate — so a configuration that has
       been dyed but not yet left has nothing stored, and `restoreInk` returned false leaving the
       copy wearing the OTHER plate's ink. Yellow lettering on a yellow body then hit the render
       engine's contrast rescue and came out black, which is the black text he saw.
         `recascadeColorDefaults` is the same derivation the dye cascade runs, and it takes a plain
       profile — so the fallback is the app's own answer to "what should ink be on this plate",
       not a guess made here. Remembered still wins: it is the buyer's actual choice. */
    if (!restoreInk({ designProfile: copy }, want)) recascadeColorDefaults(copy, { plateChange: true }); // WD 495
    return copy;
  }

  /** One middot between qualifiers. A NEW node each call — an element cannot be in two places — and
   *  `aria-hidden` because it is punctuation a screen reader should not read as a word. */
  const sep = () => h('span', { class: 'wd-qty-sep', 'aria-hidden': 'true' }, '·');

  /* ── THE CARD BUILDS THE PICTURE IT NEEDS (Ryan 2026-08-10) ─────────────────────────────────────
   * The logo a configuration shows is the artwork RECOLOURED for its plate, and that recolour is a
   * canvas job — so the first, synchronous paint can only ever be the plain artwork. Caching the
   * live pipeline's rasters got this right only for configurations the buyer had stood on, which is
   * a contract that depends on where they happen to have browsed: resume a design, go straight to
   * the quote form, and every card falls back.
   *   So the card asks for its own. `ctx.rebuildLogoComposite` is the same builder the live path and
   * the batch renderer use, it already accepts a profile, and it writes the composite onto that
   * profile's own logo objects — which are the shallow copies made above, never the live ones.
   * ⚠ ONCE PER CONTEXT, not per paint. The promise is cached under the ink key plus the artwork
   * fingerprint, so a repaint reuses it and a new upload invalidates it.
   * ⚠ The <img> is repainted by swapping `src`, so a card that is already on screen updates in place
   * rather than the panel having to rebuild. A failure leaves the plain artwork, which is visible.
   */
  const cardArt = new Map();
  function paintCardArt(item, img, size) {
    if (!ctx.rebuildLogoComposite) return;
    const copy = profileForCard(item);
    if (copy === ctx.state.designProfile) return;          // live: the composite is already right
    const want = inkContextKey(ctx.state, { body: item.plateHex, product: item.key });
    const el = (copy.elements || []).find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
    if (!el) return;
    const art = `${el.logo.dataUrl.length}:${el.logo.dataUrl.slice(-16)}`;
    const cacheKey = `${want}|${art}`;
    let job = cardArt.get(cacheKey);
    if (!job) {
      job = ctx.rebuildLogoComposite(copy)
        .then(() => {
          const lg = (copy.elements || []).find((e) => e && e.type === 'logo' && e.logo);
          return lg ? { url: lg.logo.renderUrl || null, aspect: lg.logo.renderAspect ?? null } : null;
        })
        .catch(() => null);
      cardArt.set(cacheKey, job);
    }
    job.then((raster) => {
      if (!raster || !raster.url || !img.isConnected) return;
      const withArt = profileForCard(item);
      for (const e of withArt.elements || []) {
        if (e && e.type === 'logo' && e.logo) { e.logo.renderUrl = raster.url; e.logo.renderAspect = raster.aspect; }
      }
      try { img.src = svgSrc(renderCardSvg(item, withArt, size)); } catch { /* keep what is drawn */ }
    });
  }

  function renderCardSvg(item, profile, size) {
    const geom = geomOptsFor(item.key) || {};
    return renderDesignToSVGString(profile, {
      size,
      uid: `wd-q${size}-${item.key.replace(/[^a-z0-9]/gi, '')}`,
      product: modelFor(item.key),
      weight: thumbWeight(item),
      economy: geom.economy === true,
      /* ⛔ THE CARD'S OWN COLOUR, NEVER `undefined` (Ryan 2026-08-10). `undefined` means "use the
         profile's live `bodyColor`" — i.e. whatever configuration is on the stage — so with a yellow
         and a green set BOTH cards drew the stage's green. It was invisible until a second
         configuration existed and then wrong on every card but one.
         ⚠ `plateHex`, not `plateColor`: the latter is a display decision and is deliberately null on
         a single-card product, which would fall straight back into the bug. `plateHex` is always the
         real value, and answers DEFAULT_BODY_COLOR for anything that cannot be dyed — so this one
         field replaces the branch that used to be here. */
      bodyColorOverride: item.plateHex || (productSupportsBodyColor(item.key) ? undefined : DEFAULT_BODY_COLOR),
    });
  }

  function productSvg(item, size) {
    return renderCardSvg(item, profileForCard(item), size);
  }

  const svgSrc = (svg) => `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;

  function productThumb(item) {
    try {
      /* ── CLICK TO ENLARGE (Ryan 2026-08-05: *"allow people to mouse over the SVG, click it, and
       * blow it up in a mini little pop-up thing in case they're confused as to which one is which
       * (especially if they designed multiple plate designs with only small subtle differences)"*)
       * ────────────────────────────────────────────────────────────────────────────────────────
       * A BUTTON, not a clickable image: it has to be reachable by keyboard and announce what it
       * does, and on this step the stage behind the pane is covered, so this is the only way to
       * look at the artwork again.
       *   `openModal` is reused rather than a bespoke popover, and that is the important part — it
       * brings Esc, a scrim click, a focus trap and return-focus with it. A surface that opens over
       * everything and invents its own dismissal is how you end up with a room with no door. */
      const label = `${item.label} — ${item.kind}`;
      const thumbEl = h('img', {
        class: 'wd-qty-thumb', alt: '', 'aria-hidden': 'true',
        src: svgSrc(productSvg(item, 96)),
      });
      paintCardArt(item, thumbEl, 96);
      return h('button', {
        type: 'button', class: 'wd-qty-thumb-btn',
        title: `Enlarge · ${label}`,
        'aria-label': `Enlarge the ${label} design`,
        onClick: () => {
          try {
            openModal({
              mount: ctx.el,
              title: label,
              // Glass card, corner ✕, no button row (Ryan 2026-08-09) — see `.wd-modal--glass`.
              variant: 'glass',
              closeX: true,
              body: h('div', { class: 'wd-qty-zoom' },
                h('img', { class: 'wd-qty-zoom-img', alt: `${label} design`, src: svgSrc(productSvg(item, 460)) }),
                h('p', { class: 'wd-qty-zoom-note' },
                  `Shown on the ${displayWeightText(thumbWeight(item), ctx.state.ui.unit)} ${ctx.state.ui.unit}`
                  + ' — the artwork is the same across every denomination you order.')),
              actions: [],   // the ✕ is the only exit it needs — see `closeX` in modal.js
            });
          } catch { /* never let a preview break the form */ }
        },
      },
      /* Painted twice on purpose: the plain artwork immediately, then this configuration's own
         recoloured composite the moment it is built. See `paintCardArt`. */
      thumbEl,
      /* ── THE THUMB SAYS IT IS TAPPABLE (Ryan 2026-08-09: *"on mobile, if you think it'll be
         helpful, add a zoom button/icon on these swatches so folks know they can click on them"*) ──
           It is helpful, and this is the third control on the tool to need exactly this fix: the
         layout cards and the colour swatches both went years being clickable while saying so ONLY
         through `cursor`, which is a desktop affordance a phone cannot render. Same shape here —
         `.wd-qty-thumb-btn` is `cursor: zoom-in` and nothing else, so on a phone the thumbnail is
         indistinguishable from decoration.
           A badge rather than a hover state, for the same reason: there is no hover to wait for. It
         is `aria-hidden` because the BUTTON already announces "Enlarge the <product> design" — a
         screen reader gaining a second, wordless mention of the same control would be noise, not
         information. */
      /* ⛔ `html:`, NOT NESTED `h('svg', …)` CALLS — THAT IS WHY THE FIRST ONE WAS INVISIBLE.
         `h()` builds with `createElement`, so an `<svg>` made that way lands in the HTML namespace
         and renders NOTHING: no error, no warning, no pixels. Every other icon in the tool is an SVG
         STRING handed over as `html` (`UPLOAD_ICON` in logo-step.js is the reference), and that is
         the only shape that works here. A silent no-op is exactly what Ryan reported — *"can't see
         them at all"*. */
      h('span', { class: 'wd-qty-thumb-zoom', 'aria-hidden': 'true', html: ZOOM_ICON }));
    } catch {
      return null; // a thumbnail is a courtesy; never let one stop the form rendering
    }
  }

  function renderMode() {
    /* ── THE PAIR STAYS PUT (Ryan 2026-08-05: *"when someone clicks on the I know what I need, have
     * the fields below it pop-up to enter quantities"*) ──────────────────────────────────────────
     * It used to be a chooser that vanished once answered, with separate text links to change your
     * mind. Keeping the two buttons visible and marking the active one does three jobs with one
     * control: it shows which mode you are in, it IS the way back, and the grids simply appear
     * beneath it — which is what "pop up below it" describes.
     */
    /* `MODE_CHOICE_ENABLED` is read HERE rather than at the call sites, so the chooser disappears
       from every path that could render it — first paint, a product added, a product removed — with
       one condition instead of four. See the flag's own note above. */
    const show = MODE_CHOICE_ENABLED && set.length > 0;
    modeChooser.hidden = !show;
    if (!show) return;
    /* ── THE GATE'S OWN BUTTON, NOT A LOOKALIKE (Ryan 2026-08-07: *"make [these] more like the
     * buttons on the main welcome screen so that when you mouseover them, they swipe from left to
     * right"*) ────────────────────────────────────────────────────────────────────────────────
     * They wear `.wd-gate-btn` itself, so the pill shape, the lit glass bevel and the wipe arrive
     * as the SAME rules the welcome screen runs — a copied set of declarations is how two surfaces
     * that are meant to match drift apart on the next retune.
     *
     * ⚠ THE LABEL MUST BE ITS OWN SPAN. The wipe is a `scaleX(0)` fill absolutely covering the
     * button; a bare text node would sit in the same stacking context and be painted over by it.
     * `.wd-gate-btn__t` is the layer that rides above — same two-span rig as the gate, the product
     * pills and the theme's blog CTA. */
    const btn = (mode, label, primary) => h('button', {
      type: 'button',
      class: 'wd-btn wd-gate-btn wd-qmode-btn ' + (primary ? 'wd-btn--primary' : 'wd-btn--ghost')
        + (quantityMode === mode ? ' is-active' : ''),
      'aria-pressed': quantityMode === mode ? 'true' : 'false',
      onClick: () => {
        if (quantityMode === mode) return;
        quantityMode = mode;
        // Switching to "not sure" clears whatever was typed: a half-filled grid under a
        // "talk to me first" flag is two contradictory answers, and a rep should never have to
        // guess which one is current.
        if (mode === 'rep') for (const k of Object.keys(quantities)) delete quantities[k];
        // Answering IS the fix, so clear the complaint and re-read the gate on the spot — the same
        // input/change auto-clear `field()` wires for every other control.
        clearError(modeChooser);
        syncSubmitGate();
        /* ⚠ NO rAF DEFERRAL HERE, and that was checked rather than assumed. A probe first read the
           slide as starting ~430ms late and I deferred the rebuild a frame to "unblock" it — then
           measured properly: this handler blocks for 1ms, while IDLE frames in the headless harness
           were already 260ms apart. The starvation was the software-WebGL renderer, not the build,
           so the deferral bought nothing and cost a frame of indirection. `renderSet` opens the gate
           itself (`syncGate` is its first statement). */
        renderSet();
      },
    }, h('span', { class: 'wd-gate-btn__fill', 'aria-hidden': 'true' }),
       h('span', { class: 'wd-gate-btn__t' }, label));
    replaceChildren(modeChooser, [
      /* The `*` is not decoration — it is the ONLY thing that explains a greyed submit button, and
         this question is now gated exactly like the six starred fields below (see `isComplete`). */
      h('p', { class: 'wd-qmode-q' }, 'What quantities do you need?', h('span', { class: 'wd-req' }, '*')),
      h('div', { class: 'wd-qmode-btns' },
        btn('enter', 'I know what I need', true),
        btn('rep', "I'm not sure yet", false)),
    ]);
  }

  /**
   * Open or close the whole form on the quantity answer.
   *
   * THREE classes, not one, and the middle one is the point. `is-gated` holds the tall centred box;
   * `is-ungating` keeps the SAME flex centring while `min-height` animates to 0, so the question
   * rides the shrinking box up to the top instead of jumping there the instant the class flips.
   * Dropping straight from `is-gated` to nothing would swap the layout model in one frame and the
   * slide would be a cut. Once the animation lands, BOTH come off — the flex model exists only for
   * the length of the transition, so the resting form is plain block layout, exactly as before.
   */
  function syncGate() {
    const answered = quantityMode !== null;
    /* "Talk to a rep first" renders NOTHING below the chooser — no cards, no total — so the
       quantities section's bottom margin (sized to separate a stack of product cards from the next
       heading) is left holding open a gap with nothing in it. Ryan 2026-08-07: *"the gap between the
       buttons and the YOUR DETAILS header is too big."* The margin is tightened in this state only;
       see `.wd-step--product.is-rep` in the stylesheet. */
    el.classList.toggle('is-rep', quantityMode === 'rep');
    if (!answered) {
      clearTimeout(gateTimer);
      el.classList.add('is-gated');
      el.classList.remove('is-ungating');
      formReveal.classList.remove('is-open', 'is-settled');
      /* ⚠ CLIPPED IS NOT HIDDEN. `overflow: hidden` on a 0fr track takes the form off the SCREEN and
         leaves it in the tab order and the accessibility tree — so a keyboard buyer tabbing off the
         two pills would fall into a dozen fields they cannot see, and a screen reader would read out
         a form that is not on offer yet. `inert` is the one attribute that removes both at once. */
      formReveal.setAttribute('inert', '');
      return;
    }
    /* ⛔ THE ANIMATION IS EDGE-TRIGGERED; THE OPEN STATE MUST NOT BE (fixed 2026-08-30, same day it
       shipped broken). This used to read `if (!el.classList.contains('is-gated')) return;` — i.e.
       "we are already open, nothing to do" — which was true for as long as every session STARTED
       gated and could only become answered by a click.
         `MODE_CHOICE_ENABLED = false` made `quantityMode` start as 'enter', so the very first
       `syncGate()` took this answered path, found no `is-gated` to remove (nothing had ever added
       it), and returned. `formReveal` is built with neither `is-open` nor `is-settled`, and its
       track is `0fr` until one of them lands — so the whole "Your details" section stayed collapsed
       to zero height, with no chooser left on screen to open it. The tab had no submittable form at
       all. Ryan, within the hour: *"there's literally no way to complete the form submission."*
         So: assert the open state on EVERY answered call, and play the slide only on the real
       gated → open transition. A first paint that is already answered has nothing to animate FROM
       and settles immediately. */
    const wasGated = el.classList.contains('is-gated');
    el.classList.remove('is-gated');
    formReveal.classList.add('is-open');
    formReveal.removeAttribute('inert');
    if (!wasGated) {
      // Nothing to slide: no gate was ever shown. Land in the rested, clipped state directly.
      clearTimeout(gateTimer);
      el.classList.remove('is-ungating');
      formReveal.classList.add('is-settled');
      return;
    }
    el.classList.add('is-ungating');
    clearTimeout(gateTimer);
    gateTimer = setTimeout(() => {
      el.classList.remove('is-ungating');
      // Clip off at rest, or the track shears focus rings on the edge fields — see `.wd-qty-reveal`.
      formReveal.classList.add('is-settled');
    }, GATE_SLIDE_MS);
  }

  function renderSet() {
    set = quotedSet(ctx.state);
    syncGate();
    setEmpty.hidden = set.length > 0;
    renderMode();
    /* In "talk to a rep first" the GRIDS collapse and the PRODUCTS do not. They still need to see
       what they are asking about — a section that hides the whole set reads as though the designs
       were dropped from the request. */
    /* ⚠ NOTHING BELOW THE BUTTONS UNTIL "I know what I need" (Ryan 2026-08-05: *"if they click
       'I'm not sure yet', have nothing appear in terms of the product cards.. the 'No problem...'
       line is fine"*). The "No problem…" line itself was removed on 2026-08-07, so "I'm not sure
       yet" now shows nothing at all below the buttons — the rule survives its example, and is if
       anything more literal now. So the product blocks render for exactly ONE mode, and in the other two the
       grids are not built at all rather than built and hidden — with three products that was ~45
       number inputs constructed for a buyer who has just said they would rather talk to a rep.
         ⚠ THE PRODUCTS STILL TRAVEL. `set` is resolved above, before any of this, so a rep-first
       lead still names every design the buyer made (as "wants this, quantity to be worked out with
       a rep"). What the buyer stops seeing is a wall of cards; what the rep receives is unchanged. */
    /* ⚠ THE ROWS ARE EMPTIED AFTER THE SLIDE, NOT BEFORE IT. They are deliberately not BUILT in the
       other two modes (see above — ~45 number inputs nobody asked for), and tearing them out on the
       click would collapse the wrapper to zero in one frame with nothing left to animate. So the
       track closes first and `clearRowsTimer` empties it once the transition has run.
         Cancelled on every entry: clicking back to "I know what I need" mid-collapse must not have
       a pending timer wipe the grids that were just rebuilt. */
    clearTimeout(clearRowsTimer);
    clearTimeout(settleTimer);
    const open = quantityMode === 'enter';
    if (!open) {
      // `is-settled` goes FIRST and instantly — the clip has to be back on before the track starts
      // closing, or the grids hang outside it for the whole collapse.
      setReveal.classList.remove('is-open', 'is-settled');
      clearRowsTimer = setTimeout(() => replaceChildren(setRows, []), QTY_SLIDE_MS + 40);
      syncTotal();
      return;
    }
    /* ⭐ THE RUNGS ARE REGISTERED SO OWNERSHIP CAN BE RE-DECIDED WITHOUT A RE-RENDER. Cleared
       here, refilled by the map below, and read only by `syncOwnership`. */
    rungNodes.clear();
    replaceChildren(setRows, set.map((item) => {
      const paired = item.unit === 'pair';
      const filled = () => item.weights.some((w) => quantities[`${item.key}|${w}`]);
      /* ONE ROW WHERE THE LADDER FITS IN ONE ROW (Ryan 2026-08-05: *"on the grip plates we can
       * probably fit in 6-fields wide so 2.5, 5, 10, 25, 35, and 45 can all fit on one row. same
       * with training rubber bumper plates"*). Both of those are exactly six rungs, and `auto-fill`
       * was giving them five and orphaning the sixth onto a line of its own.
       *   The count is stated rather than left to the track sizing, because a ladder of six is a
       * fact about the product and the buyer should see it as one rack, not as five plus a stray.
       * Anything longer keeps `auto-fill`, which is what a 35-rung dumbbell rack needs. */
      const oneRow = !paired && item.weights.length <= 6;
      /* A CLASS, not an inline `grid-template-columns` and not `repeat(var(--n), …)` — the latter
         does not parse (CSS wants a literal count there, and the whole rule is dropped), and the
         former would outrank the narrow-frame media query that has to un-do it. */
      const rows = h('div', {
        class: 'wd-qty-rungs' + (paired ? ' is-paired' : '')
          + (oneRow ? ` is-one-row is-cols-${item.weights.length}` : ''),
      });
      replaceChildren(rows, item.weights.map((w) => {
        const id = `${item.key}|${w}`;
        /* ── THE UNIT LIVES IN THE BOX (Ryan 2026-08-05: *"include a grayed out 'pairs' word in each
         * input box… then people can go in, type in a number… and when they type in a number, the
         * 'pairs' ungrays and turns the same font color as the number"*) ──────────────────────────
         * A number input cannot carry a suffix, so the word is a sibling positioned over the box's
         * right end and the input reserves room for it. Greyed it reads as a hint about what the
         * box wants; lit it reads as part of the answer — which is exactly the difference between
         * an empty box and a filled one, said without a second label. */
        const suffix = paired ? h('span', { class: 'wd-qty-suffix', 'aria-hidden': 'true' }, 'pairs') : null;
        const input = h('input', {
          type: 'number', class: 'wd-qty-input' + (paired ? ' has-suffix' : ''), min: '0', step: '1', inputmode: 'numeric',
          'aria-label': `${displayWeightText(w, ctx.state.ui.unit)} ${ctx.state.ui.unit} — quantity in ${paired ? 'pairs' : 'units'}`,
          value: quantities[id] ? String(quantities[id]) : '',
          onInput: (e) => {
            const n = Math.max(0, Math.floor(Number(e.target.value) || 0));
            if (n > 0) quantities[id] = n; else delete quantities[id];
            if (suffix) suffix.classList.toggle('is-lit', n > 0);
            syncTotal();
            /* ⚠ ON EVERY KEYSTROKE, and NOT via `renderSet()`. Typing 10 into the white set's 45
               is the moment the green set's 45 becomes unavailable, so the two blocks have to
               agree immediately — but re-rendering would rebuild the input being typed into and
               take the caret with it. `syncOwnership` mutates the existing nodes instead. */
            syncOwnership();
          },
        });
        if (suffix && quantities[id]) suffix.classList.add('is-lit');
        const rung = h('label', { class: 'wd-qty-rung' },
          h('span', { class: 'wd-qty-w' }, `${displayWeightText(w, ctx.state.ui.unit)} ${ctx.state.ui.unit}`),
          h('span', { class: 'wd-qty-box' }, input, suffix));
        // Registered so `syncOwnership` can reach this rung without a re-render — see its note.
        rungNodes.set(id, { rung, input, item, w });
        return rung;
      }));

      /* ── ONE CLICK PER RACK, for the product that needs it (Ryan 2026-08-05) ──────────────────
       * A dumbbell ladder is ~30 rungs and a gym buys a RACK, named by where it stops. `Add` is
       * literal: it puts one more pair on every rung in the range, so clicking 5–50 twice is two
       * racks rather than a no-op, and a 5–50 followed by a 5–100 tops up the light end the way
       * ordering a second rack actually would. */
      /* ── ONE "BULK ADD" DROPDOWN, NOT A ROW OF PILLS (Ryan 2026-08-07) ────────────────────────
       * *"Instead of all the pills for 'Two of each' and that sort of thing, what about a drop-down
       * in the top right, left of 'Not sure yet', and the drop-down can be on-brand and have
       * placeholder text of 'Bulk add'?"*
       *
       * The pills were a variable-length row of controls whose LENGTH depended on the product: one
       * for a bumper, three or four for a dumbbell, plus a "Clear" that appeared only once something
       * was typed. So the card's shape changed per product and again as you filled it in — a row
       * that grew under the buyer mid-task. A dropdown is one control of one size that says the
       * same things, and it collapses "how many shortcuts does THIS product have" into a list where
       * a varying length costs nothing.
       *
       * ⚠ IT IS A MENU OF ACTIONS WEARING A SELECT'S CLOTHES, so it never keeps a value. Every
       * option RUNS and the control returns to "Bulk add" — which is why the placeholder is a verb
       * and not a state. `renderSet()` rebuilds this whole block anyway, so the reset is structural
       * rather than a line of housekeeping that could be forgotten.
       *
       * ⚠ AND IT IS BUILT FROM THE SAME TWO BRANCHES THE PILLS HAD, unchanged in meaning:
       *   · racked products (dumbbell, fixed barbell) — one option per named rack, ADDITIVE, so
       *     picking 5–50 twice is two racks rather than a no-op (Ryan 2026-08-05);
       *   · everything else (the bumper, the grip) — "Two of each", because a plate loads a bar from
       *     both ends and nobody orders one (Ryan 2026-08-07). ⚠ Two, not one, in the QUANTITY as
       *     well as the label: a wrong number the buyer has been told is right is the worse bug.
       *   · Clearing is NOT in here — see `clearLink` below. */
      const racks = setsFor(item);
      const bulkOptions = racks.length
        ? racks.map(({ from, top }) => ({
          label: `Add ${from}–${top} lb set`,
          /* ⛔ SKIPS RUNGS A SIBLING ALREADY HOLDS. The typed boxes are disabled for those, so a
             bulk add that ignored ownership would be the ONE route back into the double-count this
             whole rule exists to stop — and a silent one, because it fills thirty boxes at once and
             nobody re-reads them. The owners are resolved ONCE for the whole run: resolving per
             rung would let this add's own writes start claiming rungs mid-loop. */
          run: () => {
            const owner = rungOwners();
            for (const w of setRungs(item, top)) {
              if (takenBySibling(item, w, owner)) continue;
              const id = `${item.key}|${w}`;
              quantities[id] = (quantities[id] || 0) + 1;
            }
          },
        }))
        : [{
          label: 'Two of each',
          run: () => {
            const owner = rungOwners();
            for (const w of item.weights) {
              if (takenBySibling(item, w, owner)) continue;
              quantities[`${item.key}|${w}`] = 2;
            }
          },
        }];
      /* `is-placeholder` is the muted treatment the customer-type select already uses — the same
         problem (a `<select>` has no `::placeholder`) answered the same way, rather than a second
         look for the same idea. It never leaves that state here, because the value never sticks. */
      const bulkSelect = h('select', {
        class: 'wd-qinput wd-qty-bulk is-placeholder',
        'aria-label': `Bulk add quantities for ${item.label}`,
        onChange: (e) => {
          const pick = bulkOptions[Number(e.target.value)];
          e.target.value = '';           // back to "Bulk add" before anything re-renders
          if (!pick) return;
          pick.run();
          renderSet();
        },
      },
      h('option', { value: '' }, 'Bulk add'),
      bulkOptions.map((o, i) => h('option', { value: String(i) }, o.label)));

      /* ── CLEARING IS ITS OWN CONTROL, IN RED (Ryan 2026-08-07) ────────────────────────────────
       * *"don't include clear all in the drop-down. Instead, once any quantity is added, include
       * 'Clear quantities' hyperlink text left of the drop-down in red, and that's what people will
       * click to clear."*
       *
       * It never belonged in that list. "Bulk add" is a menu of things that ADD, and every other
       * option is additive by design — a destructive row sitting among them is one mis-click from
       * wiping a rack somebody just typed, with no visual difference to warn them. Out here it reads
       * as what it is: a different KIND of action, in the colour the form already uses for "this is
       * the serious one".
       *
       * Per PRODUCT, like the pill it replaces — it clears the card it sits on. A single global
       * clear would appear on every card the moment any one of them was filled, and would either
       * do nothing on the empty ones or take work from cards the buyer never touched.
       *
       * A `<button>` styled as a link, not an `<a>`: it performs an action and goes nowhere, and an
       * anchor with no href is exactly the thing screen readers announce wrong. */
      const clearLink = filled()
        ? h('button', {
          type: 'button',
          class: 'wd-qty-clear',
          onClick: () => { for (const w of item.weights) delete quantities[`${item.key}|${w}`]; renderSet(); },
        }, 'Clear quantities')
        : null;

      // `quantityMode` is 'enter' by the time this runs — the other modes never build a block.
      const isTbd = tbd[item.key] === true;
      /* ── "NOT SURE YET" IS AN ANSWER (Ryan 2026-08-05: *"add a button for 'TBD' or 'Unsure' which
       * then collapses the section and allows the customer and rep to figure it out later, but at
       * least get some form of pricing now"*) ────────────────────────────────────────────────────
       * It collapses the rungs — a 30-box grid the buyer has decided not to fill is just noise —
       * and clears whatever was in them, because a half-filled grid under a TBD flag is two
       * contradictory answers and the rep would have to guess which one is current. */
      const tbdBtn = h('button', {
        type: 'button', class: 'wd-qty-tbd' + (isTbd ? ' is-on' : ''),
        'aria-pressed': isTbd ? 'true' : 'false',
        onClick: () => {
          if (isTbd) delete tbd[item.key];
          else {
            tbd[item.key] = true;
            for (const w of item.weights) delete quantities[`${item.key}|${w}`];
          }
          renderSet();
        },
      }, isTbd ? 'Enter quantities' : 'Not sure yet');

      return h('div', { class: 'wd-qty-block wd-flat-glass' + (isTbd ? ' is-tbd' : '') },
        h('div', { class: 'wd-qty-head' },
          productThumb(item),
          /* ⭐ THE COLOUR CHIP NAMES THE BLOCK when a product was picked more than once — otherwise
             the buyer is looking at two cards headed word-for-word identically and has no way to
             know which one is their white set. Absent for every single-configuration product, so
             nothing changes on a quote that does not need it. */
          /* ⛔ THE SWATCH AND THE LABEL ARE ONE LINE, so they need one BOX (Ryan 2026-08-10: *"on
             mobile it's on its own line above the header"*). `.wd-qty-name` is a flex COLUMN on the
             phone, so every direct child becomes a row — the swatch was a direct child and duly took
             a row of its own. Wrapping the pair puts them back on one line without the column having
             to know anything about them.
             ⚠ THE QUALIFIERS ARE NOW SPANS WITH THEIR OWN SEPARATORS, for the same reason in reverse:
             the middots used to live INSIDE each string (the only way to survive flex eating the
             whitespace between items), which also made them impossible to hide. On the phone Ryan
             wants one qualifier per line and no bullets at all, so the dots move into elements the
             stylesheet can switch off. */
          h('span', { class: 'wd-qty-name' },
            h('span', { class: 'wd-qty-title' },
              ...(item.plateColor
                ? [h('span', { class: 'wd-qty-swatch', style: `--wd-qty-swatch: ${item.plateColor}`, 'aria-hidden': 'true' })]
                : []),
              item.label),
            h('span', { class: 'wd-qty-kind' },
              h('span', { class: 'wd-qty-q' }, item.kind),
              ...(item.plateColor
                ? [sep(), h('span', { class: 'wd-qty-q wd-qty-color' }, `${colorWord(item.plateColor)} set`)]
                : []),
              /* ⭐ WHAT THEY ACTUALLY DESIGNED (Ryan 2026-08-10). ⚠ A LABEL, NOT A LIMIT — the grid
                 below still offers the whole ladder. Plural because a MERGED card can carry several.
                 Empty unless the product yields more than one card, so nothing new appears on an
                 ordinary single-design quote. */
              ...(item.designedRungs && item.designedRungs.length
                ? [sep(), h('span', { class: 'wd-qty-q wd-qty-designed' },
                  `designed on ${item.designedRungs.map((w) => displayWeightText(w, ctx.state.ui.unit)).join(' + ')} ${ctx.state.ui.unit}`)]
                : []))),
          /* "Bulk add" sits immediately LEFT of "Not sure yet" (Ryan 2026-08-07). The two belong
             together: both answer the whole product at once, against a grid of rungs that answers it
             one at a time. Hidden while the product is TBD — there is no grid to bulk-fill. */
          h('span', { class: 'wd-qty-head-actions' }, isTbd ? null : clearLink, isTbd ? null : bulkSelect, tbdBtn)),
        isTbd
          ? h('p', { class: 'wd-qty-tbd-note' }, "We'll work out the quantities with you — your rep will follow up.")
          : null,
        /* (The `.wd-qty-sets` pill row is GONE 2026-08-07 — every shortcut it held is an option in
           the header's "Bulk add" now. See `bulkOptions`.) */
        isTbd ? null : rows);
    }));
    /* ⚠ ON THE NEXT FRAME, NOT THIS ONE. `0fr → 1fr` only transitions if the browser has painted
       the closed state first; setting the class in the same tick the rows are appended collapses
       both into one style resolution and the grids simply appear. rAF is the cheapest way to say
       "after this paint". Harmless when already open — the class is idempotent. */
    requestAnimationFrame(() => setReveal.classList.add('is-open'));
    /* ⚠ AND THE CLIP COMES OFF ONCE IT HAS LANDED. `overflow: hidden` is what makes the slide read
       as a slide, but at rest it cuts the quantity cards' own glass shadow and any focus ring on an
       input near an edge — the track is exactly content-height, so there is no slack for either.
       Released a beat after the transition, and re-applied instantly on the way out (above). */
    settleTimer = setTimeout(() => setReveal.classList.add('is-settled'), QTY_SLIDE_MS + 40);
    syncTotal();
    syncOwnership();
  }

  /* ══ TWO CONFIGURATIONS MUST NOT BOTH CLAIM THE 45 (Ryan 2026-08-09) ═══════════════════════════
   *
   * ⛔ THIS IS WHERE THE ENFORCEMENT BELONGS, and the designer is not. The Size ladder on the
   * Customize tab decides which plate you are LOOKING AT — two configurations previewing the same
   * denomination costs nothing. This form decides what gets MANUFACTURED AND BILLED, so a rung
   * accepted in two blocks is one order shipped and invoiced twice. Ryan named the split himself.
   *
   * ⚠ OWNERSHIP IS DERIVED FROM THE QUANTITIES, NOT STORED. A rung belongs to whichever
   * configuration has a number against it, which means there is no second source of truth to drift,
   * nothing to migrate into an old save, and no way to own a rung you did not actually order.
   * Clearing the box hands it straight back — the buyer's undo is the same box they just typed in,
   * which is the only undo that needs no explaining.
   *
   * ⚠ AND IT NAMES WHERE THE RUNG WENT, rather than telling the buyer off. "On your white set" is
   * a sentence they can act on: it points at the block that has it, so the fix ("I meant to put
   * those on the green") is obvious. A bare "unavailable" would leave them hunting.
   */
  /** `${productKey}|${weight}` -> the rendered nodes, so ownership can change without a re-render. */
  const rungNodes = new Map();

  /**
   * Who holds each rung, keyed by BASE product + weight — the axis the partition actually runs on.
   * Two configurations of the competition bumper partition ITS ladder between them; a grip plate's
   * 45 has nothing to do with a bumper's.
   *
   * ⚠ A FUNCTION, NOT A CACHED MAP. Ownership changes on every keystroke and on every bulk add, and
   * a `const` holding a derived list is the shape that goes stale while every consumer keeps
   * reading it as current. Cheap enough to rebuild: a handful of products by a handful of rungs.
   */
  function rungOwners() {
    const owner = new Map();
    for (const item of set) {
      if (!item.plateColor) continue;   // single-configuration products partition nothing
      for (const w of item.weights || []) {
        if (Number(quantities[`${item.key}|${w}`]) > 0) owner.set(`${baseProductKey(item.key)}|${w}`, item);
      }
    }
    return owner;
  }

  /** True if `w` is spoken for by a DIFFERENT configuration of the same product. */
  function takenBySibling(item, w, owner = rungOwners()) {
    if (!item.plateColor) return false;
    const held = owner.get(`${baseProductKey(item.key)}|${w}`);
    return !!held && held.key !== item.key;
  }

  function syncOwnership() {
    const owner = rungOwners();
    for (const [, node] of rungNodes) {
      const { rung, input, item, w } = node;
      const held = item.plateColor ? owner.get(`${baseProductKey(item.key)}|${w}`) : null;
      const taken = takenBySibling(item, w, owner);
      rung.classList.toggle('is-taken', taken);
      input.disabled = taken;
      if (taken) {
        const where = `On your ${colorWord(held.plateColor)} set`;
        rung.title = where;
        input.setAttribute('aria-label', `${displayWeightText(w, ctx.state.ui.unit)} ${ctx.state.ui.unit} — already on your ${colorWord(held.plateColor)} set`);
        // A taken rung shows the OTHER set's colour, so the two blocks read as one partitioned
        // ladder rather than as one block that is mysteriously broken.
        rung.style.setProperty('--wd-taken-color', held.plateColor);
      } else {
        rung.removeAttribute('title');
        rung.style.removeProperty('--wd-taken-color');
        input.setAttribute('aria-label', `${displayWeightText(w, ctx.state.ui.unit)} ${ctx.state.ui.unit} — quantity in ${item.unit === 'pair' ? 'pairs' : 'units'}`);
      }
    }
  }

  function syncTotal() {
    const n = totalPieces(set, quantities);
    // The pair caveat only when a PAIRED product actually carries a quantity — on a barbell-only
    // fill "pairs counted as two" is a footnote about something that isn't there.
    const anyPairs = set.some((i) => i.unit === 'pair' && i.weights.some((w) => quantities[`${i.key}|${w}`]));
    /* ── AND WHAT IT ALL WEIGHS (Ryan 2026-08-07: *"can it also say '10 pieces - 285 lbs'"*) ──────
     * A piece count says how many boxes; the tonnage is what a buyer actually pictures, and it is
     * the number that decides freight — which is why the form asks for a ZIP.
     *   Through `displayWeightText` so a kg session reads the total in kg, the same way every rung
     * above it does. `totalWeightLb` sums in POUNDS and this converts once at the end: converting
     * per rung would round thirty times and then add up the errors. */
    const lb = totalWeightLb(set, quantities);
    const unit = ctx.state.ui.unit;
    /* ⚠ "lbs", NOT the "lb" every rung above it uses (Ryan 2026-08-07: *"It can just say '10 pieces
       - 100 lbs'"*). A rung is a NAME for a plate — the face is stamped "45 -LB-" — while this is a
       measured quantity of them, and quantities read plural in English. Scoped to this one line for
       that reason; do not "fix" the rungs to match it. (kg is already both.) */
    const unitText = unit === 'kg' ? 'kg' : 'lbs';
    const weightText = lb ? ` — ${displayWeightText(lb, unit)} ${unitText}` : '';
    qtyTotal.textContent = n
      ? `${n} piece${n === 1 ? '' : 's'}${weightText}${anyPairs ? ' · pairs counted as two' : ''}`
      : '';
    qtyTotal.hidden = !n;
    /* The countdown reads the very quantities this line just summed, so the two can never disagree
       about whether the order qualifies — one call here beats a second listener on the same input. */
    syncMoq();
    /* ⚠ AND THE GATE IS NOW A FUNCTION OF THE QUANTITIES, which it was not before 2026-08-30. Every
       path that changes a count has to re-read it or the button lags a keystroke behind the number
       explaining it — and this is the one place they all pass through. */
    syncSubmitGate();
  }

  /**
   * The target half of what `syncTotal` reports: the minimum, the shortfall, and the cleared state.
   *
   * THREE messages, not one with a zero in it. At zero, "230 lbs to go" is a countdown from nothing
   * and reads like a failure the buyer has already committed; the target on its own is the right
   * first sentence. Once anything is entered it becomes a countdown. Once it clears it SAYS so
   * rather than vanishing — a target that disappears at the moment you meet it leaves you unsure
   * whether you met it or broke it.
   */
  function syncMoq() {
    /* 'rep' renders no grid, so there is nothing to count and nobody to hold to a minimum. That mode
       is unreachable today (see `MODE_CHOICE_ENABLED`) and this line is what keeps it USABLE if it
       comes back — without it the hatch would open onto a form that can never be submitted. */
    /* ⚠ NOT FOR A REP. Rep mode is exempt from the MOQ gate (`quote-summary.js`), so counting a
       rep down toward a minimum they do not have to meet states a rule that will not be enforced.
       `quantityMode !== 'rep'` beside it is a different thing entirely — the buyer's retired
       "I'm not sure yet" branch, not the portal. */
    const show = set.length > 0 && quantityMode !== 'rep' && !isRepMode();
    moqLine.hidden = !show;
    if (!show) { syncMoqNotice(); return; }
    const unit = ctx.state.ui.unit;
    const unitText = unit === 'kg' ? 'kg' : 'lbs';
    const say = (lb) => `${displayWeightText(lb, unit)} ${unitText}`;
    const min = minimumFor(set);
    const short = moqShortfall(set, quantities);
    moqLine.classList.toggle('is-met', short === 0);
    /* ⚠ NO EM DASHES — customer-facing copy (house rule). The met state also drops the figure: once
       it is met the target is no longer actionable, and on the one-line desktop layout every word
       after "met" is competing with the piece count for the same row. */
    moqLine.textContent = short === 0
      ? 'Minimum order met ✓'
      : totalWeightLb(set, quantities) === 0
        ? `${say(min)} minimum order`
        : `${say(short)} to go to reach the ${say(min)} minimum`;
    syncMoqNotice();
  }

  /**
   * The same shortfall restated beside the button — but ONLY once the form is otherwise finished.
   *
   * That condition is the whole point, and it is Ryan's case verbatim: every star answered, nothing
   * red anywhere, and a dead button. Before that point the grey has other causes that ARE already
   * explained on screen (an empty star, no product picked), and adding a weight complaint to that
   * pile turns a form that guides into a form that nags.
   */
  function syncMoqNotice() {
    const short = set.length > 0 && quantityMode !== 'rep' ? moqShortfall(set, quantities) : 0;
    const ready = set.length > 0 && requiredFields().every((f) => f.ok);
    const show = short > 0 && ready && !submitted;
    moqNotice.hidden = !show;
    if (!show) { moqNotice.textContent = ''; return; }
    const unit = ctx.state.ui.unit;
    const unitText = unit === 'kg' ? 'kg' : 'lbs';
    const say = (lb) => `${displayWeightText(lb, unit)} ${unitText}`;
    // Names the number, the target and the way out — a disabled control that explains only that it
    // is disabled is the thing this whole element exists to avoid.
    // No em dashes: customer-facing copy (house rule).
    moqNotice.textContent = `Your details are all set. This order is ${say(short)} under the ${say(minimumFor(set))} minimum. Add quantities above and the button unlocks.`;
  }

  /* ── WHAT THE FORM REQUIRES, IN ONE TABLE ──────────────────────────────────────────────────
     THREE things ask this and none of them may hold its own answer: the submit GATE (may the button
     be pressed), the submit ATTEMPT (which field do I focus, and what do I say about it), and the
     per-field BLUR check added below. Two hand-kept copies was already one too many — a gate that
     disagrees with the validation is a button greying itself out over a field the form never
     complains about, which is precisely the silence Ryan hit.
       Evaluated fresh on every call: these read live input values.
       IN READING ORDER, which is what makes `requestQuote`'s `firstBad` the right field to focus. */
  const requiredFields = () => [
    { el: nameInput, ok: nameInput.value.trim().length > 1, msg: 'Please tell us your name.' },
    { el: emailInput, ok: EMAIL_RE.test(emailInput.value.trim()), msg: 'We need an email to send your design to.' },
    { el: phoneInput, ok: phoneInput.value.replace(/\D/g, '').length >= 10, msg: 'A phone number helps your rep reach you.' },
    { el: zipInput, ok: zipInput.value.trim().length >= 5, msg: 'ZIP lets us quote freight.' },
    { el: typeSelect, ok: !!typeSelect.value, msg: 'Residential or commercial?' },
    /* Commercial only — a residence has no organization, and the field is locked and stamped
       `n/a - residence` for them (see `syncOrgField`). Required-when-locked would hold the submit
       button down over a field nobody can type in. Mirrors the storefront's own
       `if (customerType === 'Commercial') fields.push(orgInput)`. */
    ...(typeSelect.value === 'Commercial'
      ? [{ el: orgInput, ok: orgInput.value.trim().length > 1, msg: 'Which school, gym or facility?' }]
      : []),
  ];

  /* ── A BAD ANSWER SAYS SO WHEN YOU LEAVE THE FIELD (Ryan 2026-08-07) ────────────────────────
   * *"if I type jimmy into the email instead of jimmy@gmail.com, it'll silently fail — it won't let
   * me submit the form, but it won't highlight anything in red as being improperly filled out,
   * which could throw folks off."*
   *
   * ⚠ A GAP THIS SESSION OPENED. The inline errors have always existed; they were raised by
   * `requestQuote`, i.e. by PRESSING submit. Gating the button on the same rules removed the press,
   * so the one path that produced the red outline became unreachable exactly when it was needed.
   * A disabled control with no explanation is a worse form than an enabled one that rejects you.
   *
   * ⚠ ON BLUR, AND ONLY WHEN SOMETHING WAS TYPED. Per-keystroke marks "j" as a broken email while
   * the buyer is still writing it — a form arguing with someone mid-word. An EMPTY field is left
   * alone too: tabbing through an untouched form must not paint it red. The `*` markers already say
   * those are needed, and an empty field has not FAILED, it has not been ANSWERED. So red means
   * "what is in here cannot be used", which is the only case confusing enough to need a colour.
   *
   * Clearing runs on INPUT, not blur — a correction should take the warning away the moment it
   * becomes a correction, rather than holding it until the buyer leaves a field they have fixed. */
  for (const { el: control } of requiredFields()) {
    const live = () => requiredFields().find((x) => x.el === control);
    control.addEventListener('blur', () => {
      const f = live();
      if (!f) return;
      const typed = String(control.value == null ? '' : control.value).trim();
      if (f.ok || !typed) clearError(control);
      else markError(control, f.msg);
    });
    const settle = () => { const f = live(); if (f && f.ok) clearError(control); };
    control.addEventListener('input', settle);
    control.addEventListener('change', settle);
  }

  /* ── THE SUBMIT GATE (Ryan 2026-08-07: *"gray out the submit button until enough info is included
     in the form to un-gate the submission"*) ─────────────────────────────────────────────────── */
  const isComplete = () =>
    requiredFields().every((f) => f.ok)
    /* ⚠ ADDED 2026-08-07 (Ryan): *"I just want to avoid situations where people who know what they
       need skip that question… then we have to spend extra time on the phone with them."* Unanswered
       used to submit fine and reach the rep as "⚠ question not answered — check whether they meant
       to enter quantities", which IS that phone call. Now it cannot be skipped.
         Gating the button rather than hiding the form: hiding taxes every buyer to catch the few who
       skip, and would not have closed the hole anyway — "I know what I need" with an empty grid is
       still a blank lead. */
    && quantityMode !== null
    && quotedSet(ctx.state).length > 0   // nothing picked = nothing to quote; submit refuses it too
    /* ── ⛔ AND IT NOW BLOCKS ON THE QUANTITIES, WHICH REVERSES 2026-08-05 ──────────────────────
     * This clause used to read: *"it must NOT block on the QUANTITIES themselves, which Ryan ruled
     * on 2026-08-05 ('a lead is a lead'); 'I'm not sure yet' is always one click away, so requiring
     * the ANSWER takes nothing from that ruling."*
     *
     * That ruling is SUPERSEDED, deliberately and by the same person. Ryan 2026-08-30: *"keep the
     * 'Submit quote request' grayed out until they do."* He was asked directly whether he meant to
     * overturn the earlier call and was shown its price — someone who fills in every field but
     * orders 180 lb now leaves with nothing, where before they became a lead we could ring — and
     * chose the hard block anyway.
     *
     * ⚠ THE OLD SENTENCE IS QUOTED ABOVE RATHER THAN DELETED. It was a real decision with a real
     * reason, and a reader who finds only the new rule will re-derive the old one and "fix" this
     * back. Its cost is recorded so the next person overturns it knowingly, or leaves it alone.
     *
     * ⚠ 'rep' IS EXEMPT, and that is not a loophole: that mode CLEARS every quantity by design, so a
     * set in it can never reach a minimum and this would hold shut a form with no way to open.
     * Unreachable today (`MODE_CHOICE_ENABLED`), and correct the moment it is not.
     *
     * ⚠ `moqShortfall` IS THE ONLY ARBITER — the countdown and the notice call it too. A gate
     * carrying its own copy of the arithmetic is a button that greys itself out over a number the
     * form beside it says is fine. */
    && (quantityMode === 'rep' || moqShortfall(set, quantities) === 0);

  /** Re-read the gate. Cheap, and called on every keystroke — no debounce needed for six reads. */
  function syncSubmitGate() {
    // `sending` owns the button while a POST is in flight; the gate must not re-enable it mid-send.
    if (sending) return;
    /* ⛔ AND `submitted` OUTRANKS THE GATE. Every field is still filled after a successful send, so
       `isComplete()` is still true and a bare gate re-read would hand the armed button straight
       back — which is precisely the bug. The state, not the form's contents, decides. */
    if (submitted) { paintSubmitted(); return; }
    quoteBtn.disabled = !isComplete();
    /* The notice explains THIS grey, so it is re-read with it. Typing the last required field is the
       exact moment "you are 55 lbs short" becomes the only remaining reason, and the message has to
       arrive on that keystroke rather than on the next quantity change. */
    syncMoqNotice();
  }
  let sending = false;

  /* ── the submitted latch ────────────────────────────────────────────────────────────────────
     `submitted` is the truth; localStorage is how it survives the tab; `paintSubmitted` is the
     only thing that touches the DOM. Keeping them separate is what stops a re-render (a product
     hop, a tab switch, `refresh()`) from repainting an armed form over a sent request — the same
     shape as the busy-state rule: state first, paint from state, never the other way round. */
  let submitted = null;   // null = not sent | {ref, at, shareUrl, email}

  /* ⛔ THE LATCH IS A BUYER'S FEATURE AND REP MODE SKIPS IT ENTIRELY (Ryan 2026-08-24: *"yeah no
     need for it on rep mode"*). The portal edition is a demo surface: a rep who submits once would
     meet "Your request is in — Reference RW-…" at the top of every demo afterwards, on a shared
     machine, in front of the next customer. That is worse than the problem the latch solves, and
     the problem itself does not exist for a rep — nobody is anxiously wondering whether their own
     test went through.
       ⚠ GATED AT BOTH ENDS, not just the read. A write-only gate would leave a rep's browser
     holding a record nothing ever clears, which would come back the day rep mode is toggled off on
     that machine. `isRepMode()` joins the four other things rep mode changes; see the project
     CLAUDE.md. The customer EMAIL is deliberately not gated: a submit from rep mode still creates a
     real lead for a real buyer, so the buyer still gets their copy. */
  const repMode = isRepMode();

  function readSent() {
    if (repMode) return null;
    try {
      const raw = localStorage.getItem(SENT_KEY);
      const v = raw ? JSON.parse(raw) : null;
      /* ⛔ KEYED ON `at`, NOT ON `ref`. A send that succeeded without returning a ref (the 413
         retry path) is still a send, and requiring the ref here would have quietly dropped exactly
         those buyers back onto an armed form — the one class of user this whole change exists for,
         because a design big enough to 413 is a design somebody worked on. */
      if (!v || typeof v !== 'object' || !v.at) return null;

      /* ⛔ AND A LATCH WITH NO SIGNATURE IS DROPPED, deliberately. Only latches written between the
         two deploys on 2026-08-24 look like this — the cut that shipped the card with a "Start a
         new request" button, before the release condition existed. With that button now gone there
         is no way out of such a latch, and no baseline it can be released against (a baseline
         adopted after the fact is taken from a settling pane and releases instantly — see
         `releaseLatchIfChanged`).
           Dropping it hands those buyers the armed form, which is exactly the pre-card behaviour,
         and they already have their confirmation email. A handful of people, for a few hours, get
         the old experience. The alternative was a permanently sealed quote tab. */
      if (!v.sig) {
        try { localStorage.removeItem(SENT_KEY); } catch { /* private mode */ }
        return null;
      }
      return v;
    } catch { return null; }   // private mode, quota, corrupt JSON — all mean "not sent"
  }

  function paintSubmitted() {
    const on = !!submitted;
    /* ⛔ ONE STATE CLASS ON THE ROOT, NOT `hidden` ON THREE NODES. The first cut set `.hidden` and
       it did nothing at all: `.wd-qsection` is `display:flex` and `.wd-form-reveal` is
       `display:grid`, and an explicit `display` beats the UA's `[hidden] { display:none }` on
       specificity. The form stayed on screen UNDER the sent card, still submittable, which is the
       exact bug this change exists to fix — caught by verify-quote-submit.mjs, invisible to the
       build. Anything that owns its own `display` cannot be hidden by the attribute. */
    el.classList.toggle('is-sent', on);
    if (!on) return;

    /* ⭐ SHARE, NOT VIEW (Ryan 2026-08-24: *"they literally just viewed and made the design… it'd be
       more powerful to have it be a 'Share your design' button, so before they x out, they can share
       what they made with more people to help get others excited"*). Exactly right: viewing is the
       one action with no value at this moment, and every share is a `?d=` link that opens the
       designer for somebody new.
         `shareExistingLink` takes the URL the QUOTE already stored rather than minting a fresh one —
       a second share record would be a divergent copy, so the buyer would be showing their committee
       one link while the rep opens another, and only one is attached to the deal.
       ⛔ REBUILT EVERY PAINT, never appended: `paintSubmitted` runs on every `refresh()` and an
       append would stack a new row each time. */
    replaceChildren(sentLinkRow, submitted.shareUrl
      ? h('button', {
        type: 'button',
        class: 'wd-btn wd-flat-glass wd-flat-glass--flush wd-sent__share',
        onClick: (e) => { shareExistingLink(ctx, submitted.shareUrl, e.currentTarget); },
      }, 'Share your design')
      : null);

    quoteBtn.disabled = true;
    quoteBtn.textContent = 'Request sent ✓';
  }

  function markSubmitted(rec) {
    if (repMode) return;   // see `repMode` above — no card, no record, form stays armed
    submitted = rec;
    try { localStorage.setItem(SENT_KEY, JSON.stringify(rec)); } catch { /* private mode */ }
    paintSubmitted();
  }

  /** Clear the latch and hand the form back, armed. */
  function clearSubmitted() {
    submitted = null;
    try { localStorage.removeItem(SENT_KEY); } catch { /* private mode */ }
    quoteBtn.textContent = 'Submit quote request';
    paintSubmitted();
    syncSubmitGate();
    ctx.announce?.('Ready for a new request.');
  }

  /* ── the exit: TINKERING releases the latch, there is no button ──────────────────────────────
   *
   * Ryan 2026-08-24: *"I'm wondering if the 'Start a new request' button is necessary. Our hope is
   * that if we designed the tool properly, they won't need or want to do this, and if they do, they
   * can just click the tabs at the top and go tinker around with the tool that way."*
   *
   * ⛔ THE BUTTON COULD NOT SIMPLY BE DELETED. The latch outlives a closed tab, so without a
   * release the Request Quote tab stays sealed on the card forever: a buyer could tab back and
   * redesign all they liked and never be able to quote the second thing they built. Removing the
   * button REQUIRED giving the latch a release condition.
   *
   * So the release is the tinkering itself, which is what he described. This is also the house
   * pattern — the storefront RFQ form tracks `modifiedAfterSubmission` and re-offers submission
   * when the cart changes (`custom-quote.js`); the same idea, with a design instead of a cart.
   *
   * ⚠ The one case that loses is "I submitted and want to fix a typo in my phone number" — they
   * call instead. That is the right trade: a duplicate lead over a typo is the worse outcome, and
   * a typo is exactly what the confirmation email's Your Details block is there to surface.
   */

  /* ⛔ LONG STRINGS ARE FINGERPRINTED, NOT COMPARED. A serialized profile carries the buyer's logo
     as a multi-megabyte base64 `dataUrl`, and this runs on every `refresh()`. Length + tail is the
     house pattern for exactly this (see `syncLogoRaster`'s `_rasterKey`), and the tail is what
     makes it a fingerprint rather than a length check: two different logos of identical byte length
     is a coincidence, two that also end in the same 12 characters is not. */
  const sigReplacer = (k, v) => (typeof v === 'string' && v.length > 96 ? `«${v.length}:${v.slice(-12)}»` : v);

  /** What this buyer is asking us to quote, as one comparable string. */
  function quoteSignature() {
    try {
      return JSON.stringify({
        // Every product picked, in order — a different set is a different quote.
        set: quotedSet(ctx.state).map((p) => p.key || `${p.category}|${p.kind}`),
        mode: quantityMode,
        qty: quantities,
        tbd,
        // ...and the artwork/colours/text painted on them.
        profile: serializeProfile(ctx.state.designProfile),
      }, sigReplacer);
    } catch {
      /* ⛔ NULL MEANS "CANNOT TELL", AND THE CALLER HOLDS THE LATCH. Returning something
         non-matching on a throw would clear the latch on every refresh, re-arming the form behind a
         buyer who submitted seconds ago — the original bug, restored by its own fix. */
      return null;
    }
  }

  /** Release the latch once the design or the set no longer matches what was submitted. */
  function releaseLatchIfChanged() {
    if (!submitted) return;
    const now = quoteSignature();
    if (now === null) return;   // cannot tell → hold the latch

    /* ⚠ A SIGNATURE-LESS LATCH IS NEVER COMPARED — it is dropped at boot instead (see `readSent`).
       Two cuts of "adopt one on the fly" were tried and both released the latch spuriously: taken at
       step CONSTRUCTION the baseline came from a half-assembled pane, and taken on the first
       `refresh()` it still did, because `refresh()` runs several times while the step settles. The
       adopted value was therefore compared against a later, settled one and always differed —
       the armed form straight back, i.e. the original bug restored by its own fix.
         Measured, not guessed: the signature is byte-stable across a 2.5s window once the tab is up,
       and unstable across the settling window. So the only baseline this may ever trust is one taken
       at SUBMIT time, when the design has been sitting still long enough for a buyer to fill in a
       form. That is where `sig` comes from and it is the only place it is written. */
    if (now !== submitted.sig) clearSubmitted();
  }
  /* BOTH events, deliberately. Every one of these fields carries an `autocomplete` attribute, so
     browser autofill is an expected path — and an autofill or password-manager fill can land a value
     while emitting `change` but not `input`. On `input` alone the button would then sit greyed over a
     form that is visibly complete, with nothing the buyer can do about it. `change` is the cheap
     insurance; re-reading the gate twice costs six string reads. */
  [nameInput, emailInput, phoneInput, zipInput, orgInput].forEach((n) => {
    n.addEventListener('input', syncSubmitGate);
    n.addEventListener('change', syncSubmitGate);
  });
  typeSelect.addEventListener('change', syncSubmitGate);
  syncOrgField();     // locked until a customer type is chosen — see its note
  /* ⭐ THE LATCH IS READ AT BOOT, which is the half that survives a closed tab. A buyer who sends a
     request, closes the designer and comes back an hour later lands on the sent card, not on a form
     armed with their own details. `syncSubmitGate` below paints whichever state this leaves. */
  submitted = readSent();
  /* ⚠ NO SIGNATURE WORK HERE. A latch missing its `sig` adopts one inside `releaseLatchIfChanged`,
     on the first refresh — see the note there for why capturing it at this moment released the
     latch instantly instead. */
  paintSubmitted();
  syncSubmitGate();   // starts greyed — an empty form can never be submitted

  async function requestQuote() {
    const { state, status, announce, el: rootEl } = ctx;

    // ── validation, inline and in reading order ─────────────────────────────────────────
    let firstBad = null;
    const need = (control, ok, message) => {
      if (ok) { clearError(control); return true; }
      markError(control, message);
      if (!firstBad) firstBad = control;
      return false;
    };
    // First, because this section sits ABOVE "Your details" and the checks run in reading order.
    need(modeChooser, quantityMode !== null, 'Let us know whether you have quantities in mind.');
    // The SAME table the gate and the blur check read — see `requiredFields`.
    for (const f of requiredFields()) need(f.el, f.ok, f.msg);

    if (!set.length) {
      status.error('Pick your equipment on the Pick Weights tab first — that is what we are quoting.');
      return;
    }
    /* ⛔ A QUANTITY IS NOW A REASON TO REFUSE THE LEAD (Ryan 2026-08-30) — it was not before, and the
       reversal is argued where the gate is, in `isComplete`. This spot used to carry the other half
       of that 2026-08-05 ruling; it is enforced below instead, AFTER the field checks, so that a
       form which is short on both weight and details still reports the details first. */
    if (firstBad) {
      status.error('A couple of details are missing — they are marked below.');
      firstBad.focus();
      return;
    }
    /* ⚠ THE SAME ARBITER THE BUTTON USED, asked a second time. The gate should have made this
       unreachable — the button is disabled — but the two paths have disagreed before (the blur
       check exists because gating the button removed the press that raised the errors), and a
       submit that silently posts a short order is worse than one that says why it will not. */
    if (quantityMode !== 'rep' && moqShortfall(set, quantities) > 0) {
      const unit = state.ui.unit;
      const unitText = unit === 'kg' ? 'kg' : 'lbs';
      status.error(`This order is ${displayWeightText(moqShortfall(set, quantities), unit)} ${unitText} under the ${displayWeightText(minimumFor(set), unit)} ${unitText} minimum — add quantities above.`);
      syncMoqNotice();
      moqLine.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const hasPlaceholder = state.designProfile.elements.some((e) => e.type === 'arctext' && isPlaceholderText(e.text));
    if (hasPlaceholder) {
      status.error('Some text still reads "YOUR TEXT / GOES HERE" — edit it in the Design step (or remove it) before quoting.');
      return;
    }

    const payload = captureDesign(state);
    try { sessionStorage.setItem(QUOTE_KEY, JSON.stringify(payload)); } catch { /* private mode */ }
    rootEl.dispatchEvent(new CustomEvent('wd:quote-request', { bubbles: true, detail: payload }));
    announce('Design saved — quote request captured.');
    quoteBtn.textContent = 'Sending your request…';
    sending = true;
    quoteBtn.disabled = true;

    wdTrack(WD_EVENTS.QUOTED); // fired on SUBMIT, not on success: a buyer who finished the form
    // and hit the button converted, whether or not our worker was reachable.

    /* ARTWORK FIRST, AND ITS FAILURE IS NOT THE LEAD'S PROBLEM. This is the one moment the
       customer's original file is both still in memory and known to be wanted (Ryan 2026-08-07:
       retain at submit, not at upload). It runs BEFORE the quote POST so the result can ride the
       payload — but it is awaited inside its own try, and a throw, a timeout or a rejected file all
       fall through to a submit that simply reports what is missing. Losing the picture is
       recoverable; losing a finished, priced-out lead is not, which is the same trade /wd-quote
       already makes for an oversized design. See ui/artwork-hold.js + LOGO-ARTWORK-RETENTION-SPEC.md. */
    /* ⛔ AND THE WHOLE SEND IS WRAPPED, because a throw in here freezes the only conversion action
       in the app. `sending` is what stops the gate re-enabling the button, so ANY exception between
       the label change above and the restore below leaves it greyed at "Sending your request…" for
       good — no error, no retry, no lead, and nothing on screen that admits it. That is precisely
       what `uploadArtwork`'s bare `state` did from WD 245 (2026-08-07) until 2026-08-14. Both
       callees are documented "never throws" and both mean it today; this is what makes the next one
       that quietly stops meaning it a REPORTED failure rather than a dead button. */
    let result = { ok: false, message: null };
    try {
      payload.artwork = { ...payload.artwork, ...(await uploadArtwork()) };
      result = await submitQuote(payload);
    } catch (err) {
      console.error('[weight-designer] quote send failed:', err);
    }

    /* ⛔ LATCHED ONLY ON SUCCESS. A FAILED send must leave the form armed: that is the one case
       where pressing the button again is the right thing to do, and latching it would strand a
       buyer whose request never landed. */
    if (result.ok) {
      markSubmitted({
        ref: result.ref || null,
        at: new Date().toISOString(),
        shareUrl: result.shareUrl || null,
        email: payload.contact.email,
        /* What was submitted, so `releaseLatchIfChanged` can tell "they came back to look at it"
           from "they built something else". Taken here so it describes what actually went. */
        sig: quoteSignature(),
      });
    }

    /* ⛔ THE "DESIGN SAVED." MODAL IS GONE (Ryan 2026-08-24). It was the app's ONLY answer to a
       finished submit, which is why it existed; the sent card now says the same thing, in place,
       permanently, with the reference number the modal never had. Keeping both meant a buyer
       dismissing a dialog to reveal a panel repeating it.
         ⚠ AND IT FIRED ON FAILURE TOO. It opened before `result.ok` was ever consulted, so a send
       that never reached us still announced "Design saved." and put a reassuring paragraph about
       our team reviewing it in front of someone whose request did not exist. The failure branch now
       says only the true thing, in `status.info` below.
         Its `try/finally` went with it. The wrapper the finally protected against is the one
       around the SEND above, which already catches; there is nothing between that catch and here
       that can throw, so a bare finally around no statement would only look like it was guarding
       something. ⚠ If anything awaited is ever added between the send and this line, it needs the
       try/finally back — leaving the button greyed at "Sending your request…" for good is the WD
       245 failure and it took a week of leads. */
    quoteBtn.textContent = 'Submit quote request';
    sending = false;
    syncSubmitGate();   // back to the GATE's answer, or the sent card's, not a flat re-enable

    /* ⛔ NO SUCCESS TOAST. The card is the confirmation and it is not going anywhere, so a
       transient line saying the same thing under it is the third copy of one message (Ryan
       2026-08-24). The FAILURE branch keeps its status line — there is no card in that case, and
       it is the only thing that tells the buyer to email us instead. */
    if (result.ok) {
      // Only on success: a failed send leaves the buyer's files staged so a retry does
      // not ask them to pick everything again.
      attach.clear();
      /* ⚠ THE ANNOUNCEMENT IS NOT OPTIONAL NOW THE MODAL IS GONE. A dialog takes focus and reads
         itself; a panel that swaps in behind a screen reader's cursor announces nothing at all. A
         non-sighted buyer would have been left with a button that silently changed label. */
      announce(result.ref
        ? `Request sent. Your reference is ${result.ref}.`
        : 'Request sent.');
    } else {
      status.info(result.message
        || 'Your design is saved, but we couldn’t send the request. Email sales@blueskyfitsupply.com and we’ll pick it up.');
    }
  }

  /**
   * Ship the buyer's artwork to R2 and return what the lead should say about it.
   *
   * TWO FILES, TWO JOBS: the ORIGINAL is what production needs (the processed art is a derivative,
   * and Real-ESRGAN invents detail), the ENHANCED is what the buyer approved on the plate.
   *
   * ⚠ A MISSING ORIGINAL IS NORMAL AND MUST BE SAID OUT LOUD. `retained()` is empty whenever the
   * design was RESUMED from autosave or opened from a `?d=` share link, because the bytes were never
   * persisted — the accepted cost of retaining at submit only. Reporting `originalStatus` rather
   * than just omitting the URL is the whole point: a rep who sees "not retained" knows to ask the
   * customer, and a rep who sees nothing assumes we already have it.
   *
   * Never throws.
   * @returns {Promise<object>} fields merged onto `payload.artwork`
   */
  /**
   * The buyer's own optional attachments, posted ONE PER REQUEST after the artwork.
   *
   * ⛔ NOT BUNDLED INTO THE ARTWORK CALL, and the reason is the worker's memory, not
   * tidiness: `request.formData()` buffers the entire body before the handler sees a
   * single part, so five 10MB files in one POST is 50MB resident inside a Worker to
   * stream straight back out. One file per call keeps every request inside the same
   * 10MB ceiling a lone logo has always had.
   *
   * `artifactId` is echoed back so they land in the design's own R2 folder; the
   * worker validates it and mints a fresh one if it is absent or malformed, which is
   * why the FIRST response is allowed to teach us an id we did not have.
   *
   * Never throws, and a failure is never fatal — same rule as the artwork above.
   * @returns {Promise<{attachments: object[], artifactId: ?string}>}
   */
  async function uploadAttachments(artifactId) {
    const files = attach.files();
    if (!files.length) return { attachments: [], artifactId };

    const rows = [];
    let id = artifactId;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const form = new FormData();
      if (id) form.append('artifactId', id);
      form.append('index', String(i));
      form.append('attachment', f, f.name);
      try {
        const res = await fetch(ARTWORK_ENDPOINT, { method: 'POST', body: form });
        const data = await res.json();
        if (!data || !data.ok || !data.attachment || !data.attachment.stored) continue;
        if (!id && data.artifactId) id = data.artifactId;   // first call minted the folder
        rows.push({
          url: data.attachment.url,
          fileName: data.attachment.fileName || f.name,
          bytes: data.attachment.bytes || f.size,
        });
      } catch { /* this file is lost, the rest and the lead are not */ }
    }
    return { attachments: rows, artifactId: id };
  }

  async function uploadArtwork() {
    const held = artworkHold.retained();
    /* ⛔ `ctx.state`, NOT a bare `state` — this function is a sibling of `requestQuote`, not nested
       inside it, so it never saw the `const { state } = ctx` that one destructures. The bare
       reference shipped in WD 245 and threw a ReferenceError on the FIRST line of every submit
       (module scope is strict, so it is a throw and not an undefined), which killed the send before
       either POST and left the button greyed at "Sending your request…" forever. Every other
       reference in this file outside `captureDesign(state)`'s parameter reads `ctx.state`. */
    const logoEl = ctx.state.designProfile.elements.find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
    const enhancedUrl = logoEl ? logoEl.logo.dataUrl : null;

    /* ⚠ THE EARLY RETURN NOW ASKS ABOUT THE ATTACHMENTS TOO. It used to be a pure
       "is there a logo" test, which was complete when this function only shipped the
       logo — but a design resumed from a share link has no held original AND can have
       no decodable enhanced art, and returning here would silently drop files the
       buyer had just chosen. */
    const extrasWaiting = attach.files().length > 0;
    if (!held && !enhancedUrl) {
      if (!extrasWaiting) return { originalStatus: 'no-logo' };
      const extra = await uploadAttachments(null);
      return { originalStatus: 'no-logo', artifactId: extra.artifactId, attachments: extra.attachments };
    }

    const form = new FormData();
    if (held) form.append('original', held.blob, held.fileName);
    if (enhancedUrl) {
      // The enhanced art only ever exists as a data URL in the profile; `fetch` on a data: URL is
      // the cheapest correct decode to a Blob and needs no canvas round-trip (which would re-encode
      // it a second time and quietly degrade the very file we are keeping as the record).
      try {
        const blob = await (await fetch(enhancedUrl)).blob();
        form.append('enhanced', blob, 'enhanced.png');
      } catch { /* the original is the one that matters — carry on without this */ }
    }

    try {
      const res = await fetch(ARTWORK_ENDPOINT, { method: 'POST', body: form });
      const data = await res.json();
      if (!data || !data.ok) {
        // The artwork call failed; the attachments are a separate set of requests and
        // have no reason to be abandoned with it.
        const orphan = await uploadAttachments(null);
        return { originalStatus: held ? 'upload-failed' : 'not-retained', attachments: orphan.attachments, artifactId: orphan.artifactId };
      }
      const extra = await uploadAttachments(data.artifactId || null);
      return {
        attachments: extra.attachments,
        artifactId: extra.artifactId || data.artifactId || null,
        originalUrl: data.original && data.original.stored ? data.original.url : null,
        enhancedUrl: data.enhanced && data.enhanced.stored ? data.enhanced.url : null,
        // The worker distinguishes absent / too-large / type-not-allowed / storage-error, and a rep
        // chasing a customer for a file WE rejected is a different (worse) conversation than one
        // chasing a file we never had.
        originalStatus: held ? (data.original && data.original.stored ? 'stored' : (data.original && data.original.reason) || 'unknown') : 'not-retained',
      };
    } catch {
      return { originalStatus: held ? 'upload-failed' : 'not-retained' };
    }
  }

  /**
   * POST the quote to rfq-worker. Never throws — a finished, priced-out lead must not be lost to
   * a network hiccup, and the caller has a fallback message for the buyer.
   * @returns {Promise<{ok: boolean, message?: string, shareUrl?: string}>}
   */
  async function submitQuote(payload) {
    const envelope = ctx.shareEnvelope?.();
    /* ⭐ EVERY MODEL THEY DESIGNED TRAVELS, not just the one on the stage (Ryan 2026-08-24: *"it
       only shares 1 of them on presentation mode"*). `/wd-share` has sent this since Phase 1b and
       presentation mode walks it with arrows and dots; this route never did, so the link that
       reaches the rep — and through them the customer — carried a single plate. Same builder as the
       share path rather than a second copy: "what the set IS" has been got wrong once already. */
    const set = designSet(ctx);
    const post = (design, withSet) => fetch(QUOTE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        design,
        ...(withSet && set.length ? { set } : {}),
        quote: payload,
      }),
    });
    try {
      let res = await post(envelope, true);
      /* ⛔ THE SET GOES BEFORE THE DESIGN DOES. A four-product set is four copies of the buyer's
         logo, so it is the likeliest thing to blow the router's body cap — and dropping it first
         costs the extra models while keeping the plate they were looking at. Only if that still
         413s does the design itself go, which is the older trade below. */
      if (res.status === 413 && set.length) res = await post(envelope, false);
      /* ⛔ 413 MUST NOT COST THE LEAD — the design is the half we can afford to lose. The envelope
         embeds the buyer's logo as base64, so the body scales with THEIR file: a 359KB PNG measured
         2.37MB on 2026-08-14. The worker's cap is now 6MB for this route (it was 2MB, which is the
         bug this retry was written beside), and its handler already drops an over-large design and
         keeps the lead — but that only helps for a request that gets IN. Past the body cap the
         whole thing 413s at the router and a finished, priced-out lead dies for a picture we are
         holding in three other places anyway: sessionStorage, the artwork already in R2, and the
         buyer's own open tab. So retry once with the design stripped. */
      if (res.status === 413 && envelope) res = await post(null, false);
      let data = null;
      try { data = await res.json(); } catch { /* non-JSON */ }
      /* ⭐ THE REF TRAVELS NOW (2026-08-24). `/wd-quote` has always returned the lead envelope and
         this dropped it on the floor, which is why the buyer was never shown a reference number
         while every other quote surface on the site shows one. It is the confirmation card's whole
         payload. Optional-chained: a 413 retry that loses the design still returns a lead, but a
         future response shape that does not must degrade to a card without a ref, not throw. */
      if (res.ok && data && data.ok) {
        return { ok: true, shareUrl: data.shareUrl, ref: (data.lead && data.lead.ref) || null };
      }
      return { ok: false, message: (data && data.message) || null };
    } catch {
      return { ok: false, message: null }; // offline / blocked
    }
  }

  /** Serialize the whole design + the quote form for the handoff / capture. */
  function captureDesign(state) {
    const logoEl = state.designProfile.elements.find((e) => e.type === 'logo' && e.logo && e.logo.dataUrl);
    const art = artworkOnFile(state);
    const unit = state.ui.unit;
    return {
      designId: 'wd-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8),
      createdAt: new Date().toISOString(),
      // THE SET, not a single product — one line per product with its own unit.
      // ⚠ EVERY product the buyer designed, not just the ones with numbers on them — the `.filter`
      // that used to sit here dropped a blank product out of the lead entirely.
      set: set.map((item) => ({
        category: item.category,
        kind: item.kind,
        label: item.label,
        unit: item.unit,
        /* ⭐ THE MACHINE-READABLE HALF of what `quantitySummary` says in prose. A rep reads the
           summary; anything that ever has to GROUP or price these lines needs the configuration
           itself, and re-parsing "green (#2E7D32)" out of a sentence is not that. Null on every
           single-configuration product, so existing consumers see no new field where there is no
           new fact. */
        plateColor: item.plateColor || null,
        configuration: item.plateColor ? colorWord(item.plateColor) : null,
        quantityTbd: tbd[item.key] === true || quantityMode === 'rep',
        lines: item.weights
          .map((w) => ({ weight: w, qty: Number(quantities[`${item.key}|${w}`]) || 0 }))
          .filter((l) => l.qty > 0),
      })),
      quantityMode: quantityMode || 'unanswered',
      quantitySummary: describeQuantities(set, quantities, unit, tbd, quantityMode),
      totalPieces: totalPieces(set, quantities),
      /* ⚠ POUNDS, ALWAYS, whatever `unit` says beside it — `totalWeightLb` sums the rungs and the
         rungs are pounds (design-profile: "UNIT IS A LABEL, NOT A WEIGHT"). A consumer that wants
         kg converts once, from this.
         ⛔ AND NOTHING RENDERS IT YET. The worker ignores unknown fields inside `quote` rather than
         rejecting them, so this is safe to send today and idle until the rep-facing side is built
         (Wave 3). It is here because freight is quoted on tonnage and the rep currently has to add
         the lines up by hand — and because `minimumLb` beside it is the only record of WHICH floor
         this order had to clear, which stops a later reader inferring it from the category list. */
      totalWeightLb: totalWeightLb(set, quantities),
      minimumLb: minimumFor(set),
      unit,
      contact: {
        name: nameInput.value.trim(),
        email: emailInput.value.trim().toLowerCase(),
        phone: phoneInput.value.trim(),
        organization: orgInput.value.trim(),
        zip: zipInput.value.trim(),
        /* ⚠ `city` / `state` are the lead-intake schema's OWN key names (validation.js: city
           maxLength 100, state maxLength 10) and `lead-routes` reads `payload.city` straight into
           the sold-to block. Deliberately NOT `soldCity`/`soldState` by analogy with `soldStreet`
           below — that prefix exists only for the street lines, and inventing it here would put
           these two exactly where the `soldStreet` comment warns: carried nowhere, silently. */
        city: zipCity,
        state: zipState,
        street: streetInput.value.trim(),
        residentialOrCommercial: typeSelect.value.toLowerCase(),
      },
      timeline,
      colors: designColors(state),
      notes: notesInput.value.trim(),
      // Still sent, still shown to the REP — just no longer asked of the buyer. See the note where
      // the "Your design" section used to be.
      artwork: { fileName: art.fileName, isVector: art.isVector, present: art.present },
      /* ⛔ `rightsConfirmed` IS NO LONGER SENT (Ryan 2026-08-07: *"we can check that prior to
         placing the order, don't need all the legal mumbo jumbo now on a simple inquiry"*). The
         checkbox, its submit gate and this field all went together — leaving the field behind would
         have posted a hard `false` on every quote, and the worker prints that as "NOT CONFIRMED",
         which reads as a buyer who REFUSED rather than one who was never asked.
           rfq-worker `weight-designer-quote.js` now renders an ABSENT field as "not asked", and
         still prints "NOT CONFIRMED" for an explicit `false` so historical quotes keep their
         meaning. ⚠ Re-adding a rights question here means sending the field again — the worker
         distinguishes all three states on purpose. */
      // Campaign attribution, so a weight lead can be credited in Marketing Ops like any other.
      // The worker already accepts `utm`; nothing was ever sending it.
      utm: capturedUtm(),
      profile: serializeProfile(state.designProfile),
      logo: logoEl ? { fileName: logoEl.logo.fileName, mimeType: logoEl.logo.mimeType } : null,
    };
  }

  function refresh() {
    /* FIRST, before anything paints: a design change releases the latch, and the gate below has to
       see the state it leaves behind. This is the only exit now that "Start a new request" is gone,
       and `refresh()` is the seam every return to this tab funnels through. */
    releaseLatchIfChanged();
    renderSet();
    syncTimeline();
    // Organization follows the customer type, and a RESUMED session can arrive with that already
    // answered — so the lock/stamp has to be re-derived here, not only on a live `change`.
    syncOrgField();
    syncSubmitGate();   // the picked set is half the gate, and Pick Weights can change it
  }

  /** The control the pane nav shows in its FORWARD slot on this step (ui/step-nav.js). */
  /* ⛔ `footerAction` IS WHERE THE SUBMIT BUTTON ACTUALLY LIVES ON DESKTOP. `quoteBtn` is a MOVE,
     not a copy: `createPaneNav` takes this node and mounts it in the pane's nav footer, so removing
     the form above does NOT remove the button — it would still sit in the footer of a screen that
     no longer has a form to submit. Handing back null is what makes it absent for a rep. */
  /* ⛔ `footerAction` IS THE FOOTER'S FORWARD CELL. `createPaneNav` drops whatever this returns
     into the right-hand half of a two-column grid whose left half is the Back button, so handing it
     the commit control is all it takes to put Create Quote beside "BACK / Customize Design" at 50%
     each — no new markup and no second grid.
     ⚠ For a BUYER it is still `quoteBtn`, whose own note explains it is a MOVE and never a copy.
     Null only when neither audience has a control here: a rep off the portal, where there is no
     Workstation to open. */
  return { el, refresh, footerAction: repChooser ? repChooser.action : (isRepMode() ? null : quoteBtn) };
}
